import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// import { SessionManager } from '../../utils/sessionManager';
import { useInstantTheme } from '../../contexts/InstantThemeContext';
import ManagerPinDialog from '../../components/ManagerPinDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCurrency } from '../../hooks/useCurrency';
import { useLayout } from '../../contexts/LayoutContext';
import { printReportService } from '../../services/printReportService';
import FirestorePrintService from '../../services/firestorePrintService';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  MenuItem,
} from '@mui/material';
import {
  Visibility,
  LocalShipping,
  Receipt,
  Person,
  Restaurant,
  Euro,
  CheckCircle,
  Pending,
  Cancel,
  Refresh,
  Dashboard,
  TrendingUp,
  ExitToApp,
  Print,
  Preview,
  LocationOff,
  Search,
  Done,
  Edit,
  Close,
  Payment,
  ShoppingBag,
  TableRestaurant,
  DeliveryDining,
  FilterList,
  AddShoppingCart,
  LocalOffer,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { Order } from '../../services/api';
import LiveDriverMap from '../../components/LiveDriverMap';
import EditOrderItemModal from '../../components/EditOrderItemModal';
import { getCurrentUser } from '../../services/currentUserHelper';
import {
  isRevenueOrder,
  isActiveOrder,
  REVENUE_TERMINAL_STATUSES,
  getBusinessDayStart,
} from '../../utils/reportingUtils';

// Time slot filter options for the Orders toolbar
const TIME_SLOTS = [
  { value: '06-12', label: 'Morning 06–12', labelAr: 'الصباح 06–12', start: 6,  end: 12 },
  { value: '12-17', label: 'Lunch 12–17',   labelAr: 'الغداء 12–17',  start: 12, end: 17 },
  { value: '17-23', label: 'Dinner 17–23',  labelAr: 'المساء 17–23',  start: 17, end: 23 },
  { value: '23-06', label: 'Night 23–06',   labelAr: 'الليل 23–06',   start: 23, end: 6  },
];

// Shared style for every row-action icon button so the Orders page has a single
// consistent visual language (same size, radius, hover). Color + bg differ per
// action intent (green=positive, red=destructive, blue=neutral, etc).
const actionBtnSx = (fg: string, bg: string, hoverBg?: string) => ({
  width: 26,
  height: 26,
  borderRadius: 1.5,
  color: fg,
  backgroundColor: bg,
  flexShrink: 0,
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: hoverBg || fg,
    color: '#fff',
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 10px ${fg}40`,
  },
  '& svg': { fontSize: 14 },
});

const ModernOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { hasSharedSidebar } = useLayout();
  const money = useCurrency();

  // Debug: Log language changes
  React.useEffect(() => {
    console.log('🌐 Orders Page - Current language:', language);
    console.log('🌐 Orders Page - Translation test:', t('orders.assign_delivery_driver'));
  }, [language, t]);
  const { appTheme } = useInstantTheme();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeliveryDialog, setOpenDeliveryDialog] = useState(false);
  const [openEditItemModal, setOpenEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<{ item: any; index: number } | null>(null);
  const [openEditCustomerModal, setOpenEditCustomerModal] = useState(false);
  const [editingCustomerName, setEditingCustomerName] = useState('');
  const [editingCustomerAddress, setEditingCustomerAddress] = useState('');
  const [updatingCustomer, setUpdatingCustomer] = useState(false);
  // Apply Discount dialog (POS-only — applies a discount to any existing order)
  const [discountDialog, setDiscountDialog] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });

  // Void-item dialog (any order type — delivery/pickup/QR/dine-in)
  const [voidItemTarget, setVoidItemTarget] = useState<{
    orderId: string;
    orderNumber: string;
    itemIndex: number;
    itemLabel: string;
    itemPrice: number;
  } | null>(null);
  const [voidItemReason, setVoidItemReason] = useState('');
  const [voidItemBusy, setVoidItemBusy] = useState(false);

  const handleVoidOrderItem = async () => {
    if (!voidItemTarget) return;
    if (!voidItemReason.trim()) {
      alert('Please enter a reason for removing this item.');
      return;
    }
    setVoidItemBusy(true);
    try {
      let cashierEmail = '';
      try {
        const raw = localStorage.getItem('saheeh_pos_user');
        if (raw) cashierEmail = JSON.parse(raw).email || '';
      } catch { /* fallback */ }
      const { voidOrderItem } = await import('../../services/voidItemService');
      const result = await voidOrderItem({
        orderId: voidItemTarget.orderId,
        itemIndex: voidItemTarget.itemIndex,
        reason: voidItemReason.trim(),
        cashierEmail,
      });
      if (!result.success) {
        alert(result.error || 'Failed to remove item.');
        return;
      }
      setVoidItemTarget(null);
      setVoidItemReason('');
      // The orders subscription will pick up the new state automatically;
      // close the detail dialog if the order ended up cancelled.
      if (result.orderCancelled) {
        setOpenViewDialog(false);
        setSelectedOrder(null);
      }
    } catch (err) {
      console.error('[VOID] failed:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setVoidItemBusy(false);
    }
  };
  const [discountTypeInput, setDiscountTypeInput] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValueInput, setDiscountValueInput] = useState('');
  const [discountReasonInput, setDiscountReasonInput] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string | null>(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);

  // Manager PIN dialog state — fraud prevention #2
  const [managerPinOpen, setManagerPinOpen] = useState(false);
  const [managerPinAction, setManagerPinAction] = useState<{
    label: string;
    requireReason?: boolean;
    onApproved: (managerEmail: string, reason: string) => void;
  } | null>(null);
  const [driverLocation, setDriverLocation] = useState<{ latitude?: number; longitude?: number; timestamp?: string; is_online?: boolean } | null>(null);
  // const [deliveryNotes, setDeliveryNotes] = useState('');
  // Order notification removed
  const previousOrdersRef = useRef<Map<string, string>>(new Map()); // Track previous statuses
  const isFirstSnapshotRef = useRef(true); // Skip notifications on first snapshot load
  
  // Arabic language flag
  const isAr = language === 'ar';

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const searchFilter = 'all' as const; // Always search across all fields
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'delivery' | 'pickup' | 'dine_in'>('all');
  const [timeFilter, setTimeFilter] = useState<string>('');
  const sortBy: 'newest' | 'oldest' | 'price_high' | 'price_low' = 'newest';
  const searchDateFrom = '';
  const searchDateTo = '';

  // Date range filter states
  const [dateRange, setDateRange] = useState<'today' | 'yesterday' | '7days' | '30days' | 'custom'>('today');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Compute the start/end Date objects for the selected date range using 6 AM business day cutoff
  const getDateRangeBounds = React.useCallback((range: typeof dateRange, fromStr?: string, toStr?: string): { start: Date; end: Date | null } => {
    const now = new Date();
    // "Today" starts at 6 AM. If it's before 6 AM, the business day started at 6 AM yesterday.
    const todayBusinessStart = new Date(now);
    todayBusinessStart.setHours(6, 0, 0, 0);
    if (now.getHours() < 6) {
      todayBusinessStart.setDate(todayBusinessStart.getDate() - 1);
    }

    switch (range) {
      case 'today':
        return { start: todayBusinessStart, end: null };
      case 'yesterday': {
        const yesterdayStart = new Date(todayBusinessStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        return { start: yesterdayStart, end: todayBusinessStart };
      }
      case '7days': {
        const sevenDaysAgo = new Date(todayBusinessStart);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { start: sevenDaysAgo, end: null };
      }
      case '30days': {
        const thirtyDaysAgo = new Date(todayBusinessStart);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return { start: thirtyDaysAgo, end: null };
      }
      case 'custom': {
        const from = fromStr ? new Date(fromStr + 'T06:00:00') : todayBusinessStart;
        const to = toStr ? new Date(toStr + 'T06:00:00') : null;
        // If 'to' date is provided, add 1 day at 6 AM to include the full business day
        const toEnd = to ? new Date(to.getTime() + 24 * 60 * 60 * 1000) : null;
        return { start: from, end: toEnd };
      }
      default:
        return { start: todayBusinessStart, end: null };
    }
  }, []);

  // Notification functions removed

  // Fetch real drivers from Firebase Firestore
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setLoadingDrivers(true);
        console.log('🚗 [DRIVER ASSIGNMENT] ========================================');
        console.log('🚗 [DRIVER ASSIGNMENT] STEP 1: Fetching drivers from Firebase...');
        console.log('🚗 [DRIVER ASSIGNMENT] Collection: drivers');
        console.log('🚗 [DRIVER ASSIGNMENT] Filter: is_active == true');

        const { collection, query, where, getDocs } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        const driversRef = collection(db, 'drivers');
        // Query without orderBy to avoid index requirement - we'll sort in JavaScript
        const q = query(
          driversRef,
          where('is_active', '==', true)
        );

        console.log('🚗 [DRIVER ASSIGNMENT] Executing Firestore query...');
        const snapshot = await getDocs(q);
        console.log(`🚗 [DRIVER ASSIGNMENT] Query result: ${snapshot.size} drivers found`);

        const driversList: any[] = [];

        snapshot.forEach((doc) => {
          const data = doc.data();
          const driverInfo = {
            id: doc.id,
            name: data.name || data.email || 'Unknown Driver',
            email: data.email || '',
            phone: data.phone || '',
            status: data.status || 'offline',
            // CRITICAL: Default to false (offline) if field is missing - rider must explicitly toggle online
            is_available: data.is_available === true,
            vehicle_type: data.vehicle_type || 'bicycle',
            rating: data.rating || 5.0,
            deliveries_today: data.deliveries_today || 0,
          };
          driversList.push(driverInfo);

          console.log(`🚗 [DRIVER ASSIGNMENT] Driver loaded:`, {
            id: driverInfo.id,
            name: driverInfo.name,
            email: driverInfo.email,
            status: driverInfo.status,
            is_available: driverInfo.is_available,
            vehicle: driverInfo.vehicle_type,
          });
        });

        // Merge Realtime Database status for each driver (source of truth for availability)
        const emailToKey = (email: string) => email.toLowerCase().replace(/[^a-z0-9]/g, '_');
        await Promise.all(
          driversList.map(async (d, idx) => {
            if (!d.email) return;
            try {
              const key = emailToKey(d.email);
              const url = `https://__SAHEEH_RTDB_UNSET__/drivers/${key}.json`;
              const res = await fetch(url);
              if (!res.ok) return;
              const rt = await res.json();
              if (rt && (rt.status || typeof rt.is_available === 'boolean')) {
                driversList[idx] = {
                  ...d,
                  status: rt.status || d.status,
                  is_available: typeof rt.is_available === 'boolean' ? rt.is_available : d.is_available,
                };
                console.log('🔄 [DRIVER ASSIGNMENT] RTDB merge:', { email: d.email, status: driversList[idx].status, is_available: driversList[idx].is_available });
              }
            } catch (e) {
              console.warn('⚠️ [DRIVER ASSIGNMENT] RTDB status fetch failed for', d.email, e);
            }
          })
        );

        // Sort drivers by name in JavaScript (no index required)
        driversList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        const availableCount = driversList.filter(d => d.is_available && d.status === 'available').length;
        const busyCount = driversList.filter(d => !d.is_available || d.status !== 'available').length;

        console.log(`✅ [DRIVER ASSIGNMENT] STEP 1 COMPLETE:`, {
          total: driversList.length,
          available: availableCount,
          busy_offline: busyCount,
        });
        console.log('🚗 [DRIVER ASSIGNMENT] ========================================');

        setDrivers(driversList);
      } catch (error) {
        console.error('❌ [DRIVER ASSIGNMENT] STEP 1 FAILED: Error fetching drivers:', error);
        // Fallback to empty array
        setDrivers([]);
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchDrivers();
  }, []);

  // Fetch orders when component mounts / date range changes and set up real-time listener
  useEffect(() => {
    console.log('📋 [ORDERS] Setting up listener for date range:', dateRange);
    setLoading(true);

    // Set up real-time listener for the selected date range — backed by Supabase.
    const setupRealtimeListener = async () => {
      try {
        const { subscribeToOrders } = await import('../../services/supabaseOrderService');
        const { start: rangeStart, end: rangeEnd } = getDateRangeBounds(dateRange, customDateFrom, customDateTo);

        isFirstSnapshotRef.current = true;

        const unsubscribe = subscribeToOrders(
          { start: rangeStart, end: rangeEnd ?? undefined, limit: 500 },
          (incoming) => {
            setLoading(false);
            const currentStatuses = new Map<string, string>();
            const ordersData: Order[] = incoming.map((o) => {
              currentStatuses.set(o.id, o.status);
              return o;
            });

            // Preserve the original status-change ref handling
            previousOrdersRef.current = new Map(currentStatuses);
            isFirstSnapshotRef.current = false;

            setOrders(ordersData);
            applySearchFilter(ordersData, searchQuery, searchFilter, sortBy, searchDateFrom, searchDateTo);
          },
        );
        return unsubscribe;
      } catch (error) {
        console.error('❌ [ORDERS] Failed to set up real-time listener:', error);
        return () => { };
      }
    };

    // Legacy Firestore code-path (dead — kept only so the surrounding
    // useEffect cleanup still compiles cleanly). Will be removed in the
    // next pass once the rest of the page is on Supabase.
    const _LEGACY = async () => {
      try {
        // Disabled — see Supabase-backed listener above.
        return () => { };
      } catch {
        return () => { };
      }
    };
    void _LEGACY;

    /* eslint-disable */
    if (false) {
        const ordersData: Order[] = [];
        const currentStatuses = new Map<string, string>();
        const querySnapshot = { forEach: (_cb: any) => {} } as any;
          querySnapshot.forEach((doc: any) => {
            const data = doc.data();
            const paymentStatus = data.payment_status || 'pending';
            const orderStatus = data.status || 'pending';

            const isCancelled = orderStatus === 'cancelled';
            const hasValidStatus = ['pending', 'confirmed', 'preparing', 'ready', 'assigned', 'picked_up', 'in_transit', 'on_delivery', 'delivered', 'completed'].includes(orderStatus);
            const hasCompletedPayment = paymentStatus === 'completed';

            if (isCancelled) return;
            if (!hasValidStatus && !hasCompletedPayment) return;

            const orderId = doc.id;
            const orderNumber = data.order_number || doc.id;
            const customerName = data.customer_name || 'Walk-in Customer';
            const currentStatus = orderStatus;
            currentStatuses.set(orderId, currentStatus);

            ordersData.push({
              id: doc.id,
              order_number: orderNumber,
              customer_name: customerName,
              // CRITICAL: Include all customer detail fields
              customer_first_name: data.customer_first_name || '',
              customer_last_name: data.customer_last_name || '',
              customer_phone: data.customer_phone || '',
              customer_address: data.customer_address || '',
              customer_postcode: data.customer_postcode || '',
              customer_city: data.customer_city || '',
              items: Array.isArray(data.items) ? data.items.map((item: any) => ({
                id: item.id || item.menu_item_id || '',
                name: item.name || item.menu_item_name || item.menu_item?.name || 'Item',
                quantity: item.quantity || 1,
                price: parseFloat(String(item.price || item.unit_price || 0)),
                unit_price: parseFloat(String(item.unit_price || item.price || 0)),
                total: parseFloat(String(item.total || item.price || item.unit_price || 0)),
                addons: item.addons || [], // Include add-ons from order items
                notes: item.notes || '', // Include notes from order items
              })) : [],
              total: typeof data.total === 'number' ? data.total : parseFloat(String(data.total || '0')),
              status: currentStatus,
              created_at: data.created_at?.toDate?.()?.toISOString() ||
                (data.created_at?.toISOString ? data.created_at.toISOString() :
                  (data.created_at || new Date().toISOString())),
              notes: data.notes || data.delivery_instructions || '',
              created_by: data.created_by || data.staff_name || 'Unknown',
              order_type: data.order_type || 'dine_in',
              source: data.source || 'pos',
              table_number: data.table_number || null,
              delivery_address: data.delivery_address || '',
              delivery_latitude: data.delivery_latitude || null,
              delivery_longitude: data.delivery_longitude || null,
              location_confirmed: data.location_confirmed || false,
              location_confirmed_at: data.location_confirmed_at || null,
              location_confirmed_by: data.location_confirmed_by || null,
              location_confirmed_by_name: data.location_confirmed_by_name || null,
              // CRITICAL: Include payment_method so invoices can display it correctly
              payment_method: data.payment_method || 'cash',
              payment_status: data.payment_status || 'completed',
              // Split table fields
              is_split_payment: data.is_split_payment || false,
              split_person_name: data.split_person_name || '',
              split_person_index: data.split_person_index || 0,
              split_total_people: data.split_total_people || 0,
              split_session_id: data.split_session_id || '',
              original_order_number: data.original_order_number || '',
              original_order_id: data.original_order_id || '',
              partial_split: data.partial_split || false,
              original_total: data.original_total || null,
              original_items: data.original_items || null,
              original_item_count: data.original_item_count || null,
              partial_split_applied: data.partial_split_applied || false,
              discount_amount: data.discount_amount || 0,
              discount_type: data.discount_type || '',
              discount_value: data.discount_value || 0,
              discount_reason: data.discount_reason || '',
              total_before_discount: data.total_before_discount || 0,
            } as Order & { rider_email?: string; assigned_driver_name?: string; delivered_at?: string; completed_at?: string; customer_first_name?: string; customer_last_name?: string; customer_phone?: string; customer_address?: string; customer_postcode?: string; customer_city?: string; payment_method?: string; payment_status?: string; delivery_latitude?: number | null; delivery_longitude?: number | null; location_confirmed?: boolean; location_confirmed_at?: any; location_confirmed_by?: string | null; location_confirmed_by_name?: string | null });
          });

          // Update previous statuses ref and mark first snapshot as done
          previousOrdersRef.current = new Map(currentStatuses);
          isFirstSnapshotRef.current = false;

          // Sort client-side if orderBy failed - Sort by date/time (newest first) with proper timestamp handling
          ordersData.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            // If timestamps are equal, sort by order number as tiebreaker
            if (dateB === dateA) {
              return (b.order_number || b.id).localeCompare(a.order_number || a.id);
            }
            return dateB - dateA; // Descending order (newest first)
          });

          setOrders(ordersData);
          applySearchFilter(ordersData, searchQuery, searchFilter, sortBy, searchDateFrom, searchDateTo);
    }
    /* eslint-enable */

    let unsubscribe: (() => void) | undefined;
    setupRealtimeListener().then((cleanup) => {
      unsubscribe = cleanup;
    });

    // Cleanup on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dateRange, customDateFrom, customDateTo, getDateRangeBounds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch orders from Firebase with user permissions
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      // Import Firebase services
      const { collection, getDocs, orderBy, query, where, Timestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      // Compute date range bounds using 6 AM business day cutoff
      const { start: rangeStart, end: rangeEnd } = getDateRangeBounds(dateRange, customDateFrom, customDateTo);
      const startTimestamp = Timestamp.fromDate(rangeStart);

      const ordersRef = collection(db, 'orders');
      const { limit: firestoreLimit } = await import('../../services/firestoreCompat');

      let q;
      try {
        if (rangeEnd) {
          const endTimestamp = Timestamp.fromDate(rangeEnd);
          q = query(ordersRef, where('created_at', '>=', startTimestamp), where('created_at', '<', endTimestamp), orderBy('created_at', 'desc'), firestoreLimit(500));
        } else {
          q = query(ordersRef, where('created_at', '>=', startTimestamp), orderBy('created_at', 'desc'), firestoreLimit(500));
        }
      } catch (e: any) {
        // Fallback if index missing
        q = query(ordersRef, orderBy('created_at', 'desc'), firestoreLimit(100));
      }

      const querySnapshot = await getDocs(q);
      console.log('📋 [ORDERS] Loaded', querySnapshot.size, 'orders (range:', dateRange, ')');

      const ordersData: Order[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as any; // Firestore data type

        // CRITICAL: Filter out cancelled orders only
        const paymentStatus = data.payment_status || 'pending';
        const orderStatus = data.status || 'pending';

        // CRITICAL FIX: Show ALL confirmed orders and completed payments
        // Only filter out cancelled orders
        const isCancelled = orderStatus === 'cancelled';
        
        // Show order if:
        // 1. Not cancelled AND
        // 2. (Status is pending/confirmed/preparing/ready/delivered/completed) OR (Payment is completed)
        // CRITICAL: Include 'pending' status to show customer app orders
        const hasValidStatus = ['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'completed'].includes(orderStatus);
        const hasCompletedPayment = paymentStatus === 'completed';
        
        // Exclude only cancelled orders
        if (isCancelled) return;
        if (!hasValidStatus && !hasCompletedPayment) return;

        // Extract proper order data from Firestore
        const orderNumber = data.order_number || doc.id;
        const customerName = data.customer_name ||
          (data.customer?.name ||
            (data.customer?.first_name && data.customer?.last_name
              ? `${data.customer.first_name} ${data.customer.last_name}`
              : 'Walk-in Customer'));

        ordersData.push({
          id: doc.id,
          order_number: orderNumber,
          customer_name: customerName,
          // CRITICAL: Include all customer detail fields
          customer_first_name: data.customer_first_name || '',
          customer_last_name: data.customer_last_name || '',
          customer_phone: data.customer_phone || '',
          customer_address: data.customer_address || '',
          customer_postcode: data.customer_postcode || '',
          customer_city: data.customer_city || '',
          items: Array.isArray(data.items) ? data.items.map((item: any) => ({
            id: item.id || item.menu_item_id || '',
            name: item.name || item.menu_item_name || item.menu_item?.name || 'Item',
            quantity: item.quantity || 1,
            unit_price: parseFloat(String(item.unit_price || item.price || 0)),
            price: parseFloat(String(item.price || item.unit_price || 0)),
            total: parseFloat(String(item.total || item.price || item.unit_price || 0)),
            addons: item.addons || [], // Include add-ons from order items
            notes: item.notes || '', // Include notes from order items
          })) : [],
          total: typeof data.total === 'number' ? data.total : parseFloat(String(data.total || '0')),
          status: data.status || 'pending',
          created_at: data.created_at?.toDate?.()?.toISOString() ||
            (data.created_at?.toISOString ? data.created_at.toISOString() :
              (data.created_at || new Date().toISOString())),
          notes: data.notes || data.delivery_instructions || '',
          created_by: data.created_by || data.staff_name || 'Unknown',
          created_by_email: data.created_by_email || '',
          created_by_name: data.created_by_name || data.created_by || '',
          order_type: data.order_type || 'dine_in',
          source: data.source || 'pos',
          table_number: data.table_number || null,
          delivery_address: data.delivery_address || '',
          delivery_latitude: data.delivery_latitude || null,
          delivery_longitude: data.delivery_longitude || null,
          location_confirmed: data.location_confirmed || false,
          location_confirmed_at: data.location_confirmed_at || null,
          location_confirmed_by: data.location_confirmed_by || null,
          location_confirmed_by_name: data.location_confirmed_by_name || null,
          // CRITICAL: Include payment_method so invoices can display it correctly
          payment_method: data.payment_method || 'cash',
          payment_status: data.payment_status || 'completed',
          discount_amount: data.discount_amount || 0,
          discount_type: data.discount_type || '',
          discount_value: data.discount_value || 0,
          discount_reason: data.discount_reason || '',
          total_before_discount: data.total_before_discount || 0,
        } as Order & { rider_email?: string; assigned_driver_name?: string; payment_method?: string; payment_status?: string; delivery_latitude?: number | null; delivery_longitude?: number | null; location_confirmed?: boolean; location_confirmed_at?: any; location_confirmed_by?: string | null; location_confirmed_by_name?: string | null });
      });

      // Sort client-side if orderBy failed - Sort by date/time (newest first) with proper timestamp handling
      ordersData.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        // If timestamps are equal, sort by order number as tiebreaker
        if (dateB === dateA) {
          return (b.order_number || b.id).localeCompare(a.order_number || a.id);
        }
        return dateB - dateA; // Descending order (newest first)
      });

      console.log(`📋 [ORDERS] Loaded ${ordersData.length} orders`);
      setOrders(ordersData);
      // Apply search filter to new orders
      applySearchFilter(ordersData, searchQuery, searchFilter, sortBy, searchDateFrom, searchDateTo);
      console.log('📋 [ORDERS] Orders state updated');

    } catch (err) {
      console.error('❌ [ORDERS] Failed to fetch orders from Firebase:', err);
      console.error('❌ [ORDERS] Error details:', {
        message: (err as Error).message,
        stack: (err as Error).stack,
        name: (err as Error).name
      });

      // Fallback: Try Django API if Firebase fails
      console.log('🔄 [ORDERS] Trying Django API as fallback...');
      try {
        const response = await fetch('https://__SAHEEH_LEGACY_BACKEND_UNSET__/api/v1/orders/orders/');

        if (response.ok) {
          const data = await response.json();
          const ordersData = (data.results || []).map((order: any) => {
            console.log('🔍 [ORDERS DEBUG] Order from Django API:', {
              id: order.id,
              order_number: order.order_number,
              customer_name: order.customer_name,
              customer: order.customer
            });

            return {
              id: order.id,
              order_number: order.order_number,
              customer_name: order.customer_name || (order.customer?.first_name && order.customer?.last_name
                ? `${order.customer.first_name} ${order.customer.last_name}`.trim()
                : 'No name entered'),
              items: order.items || [],
              total: parseFloat(order.total || (Math.random() * 50 + 10).toFixed(2)),
              status: order.status || 'pending',
              created_at: order.created_at || new Date().toISOString(),
              notes: order.notes || '',
            };
          });
          setOrders(ordersData);
          console.log(`📋 [ORDERS] Found ${ordersData.length} orders in Django API (fallback)`);
        } else {
          console.error('❌ [ORDERS] Django API also failed');
          setError(t('orders.error_load_failed'));
        }
      } catch (djangoError) {
        console.error('❌ [ORDERS] Django API fallback failed:', djangoError);
        setError(t('orders.error_load_failed'));
      }
    } finally {
      setLoading(false);
      console.log('📋 [ORDERS] Loading state set to false');
    }
  };

  const handleRefresh = () => {
    console.log('🔄 [ORDERS] Manual refresh triggered');
    fetchOrders();
  };

  // One-click direct print to the Epson cashier printer — no HTML preview.
  // Builds the same receipt payload and calls firestorePrintService so the
  // print-relay picks it up and prints on 192.168.178.46.
  const handleDirectPrintReceipt = async (order: Order) => {
    try {
      console.log('🧾 [DIRECT PRINT] Printing to Epson for order:', order.order_number || order.id);

      const orderDate = new Date(order.created_at);
      const formattedDate = orderDate.toLocaleDateString('en-US');
      const formattedTime = orderDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      const orderType = (order as any).order_type || 'dine_in';
      const total = order.total || 0;

      const isEmptyName = (n: string | undefined | null) => !n || !n.trim() || n.trim() === 'Laufkunde' || n.trim() === 'Walk-in Customer';
      const customerAddress = (order as any).customer_address || order.delivery_address || '';
      const tableNumber = (order as any).table_number || '';
      let customerName = order.customer_name;
      if (isEmptyName(customerName) && !customerAddress) {
        customerName = orderType === 'takeaway' ? 'Abholung'
          : orderType === 'delivery' ? 'Lieferung'
          : tableNumber ? `Tisch ${tableNumber}` : 'Dine-In';
      } else if (isEmptyName(customerName)) {
        customerName = orderType === 'dine_in'
          ? (tableNumber ? `Tisch ${tableNumber}` : 'Dine-In')
          : 'Bestellung';
      }

      const items = (order.items || []).map((item: any, idx: number) => {
        const qty = item.quantity || 1;
        const unit = item.unit_price ?? item.price ?? (item.total && qty ? item.total / qty : 0);
        const addonsTotal = (item.addons || []).reduce(
          (s: number, a: any) => s + ((a.price || 0) * (a.quantity || 1)),
          0,
        );
        return {
          name: item.name || item.item_name || item.menu_item_name || 'Artikel',
          quantity: qty,
          price: unit,
          base_price: Math.max(0, unit - addonsTotal),
          total: unit * qty,
          category_name: item.category_name || item.category || '',
          addons: item.addons || [],
          notes: item.notes || '',
          itemNumber: idx + 1,
        };
      });

      const explicitDiscount = (order as any).discount_amount || 0;
      const explicitTotalBefore = (order as any).total_before_discount || 0;
      let deliveryFeeAmt = (order as any).delivery_fee || 0;
      const discountReason = (order as any).discount_reason || '';
      const discountType = (order as any).discount_type || '';
      const discountValue = (order as any).discount_value || 0;

      // Infer discount/delivery fee from items vs total for older orders
      // that never persisted the discount_*/delivery_fee fields.
      const itemsGross = items.reduce((s, it) => s + it.total, 0);
      const gap = total - (itemsGross + deliveryFeeAmt);
      let impliedDiscount = 0;
      if (explicitDiscount === 0 && Math.abs(gap) > 0.01) {
        if (gap < 0) {
          impliedDiscount = -gap;
        } else if (gap > 0 && deliveryFeeAmt === 0 && orderType === 'delivery') {
          deliveryFeeAmt = gap;
        }
      }

      const discountAmount = explicitDiscount > 0 ? explicitDiscount : impliedDiscount;
      const totalBeforeDiscount = explicitTotalBefore > 0
        ? explicitTotalBefore
        : (discountAmount > 0 ? itemsGross + deliveryFeeAmt : total);

      // Netto/VAT rendered as the PRE-discount values so the printed BELEG
      // shows Zwischensumme → Rabatt → Gesamt brutto. The relay's receipt
      // generator handles the discount line format.
      const vatRate = orderType === 'dine_in' ? 1.19 : 1.07;
      const preDiscountGross = discountAmount > 0
        ? totalBeforeDiscount
        : total;
      const subtotal = Math.round((preDiscountGross / vatRate) * 100) / 100;
      const tax = Math.round((preDiscountGross - subtotal) * 100) / 100;

      const receiptData: any = {
        orderNumber: order.order_number || order.id,
        customerName,
        customerAddress,
        customerPhone: (order as any).customer_phone || '',
        orderDate: formattedDate,
        orderTime: formattedTime,
        items,
        subtotal,
        tax,
        total,
        paymentMethod: (order as any).payment_method || 'cash',
        paymentStatus: 'paid',
        order_type: orderType,
        tableNumber: tableNumber || null,
        portal: orderType === 'takeaway' ? 'takeaway.com' : undefined,
        isCopy: false,
        delivery_fee: deliveryFeeAmt,
        ...(discountAmount > 0 ? {
          discount_amount: discountAmount,
          discount_reason: discountReason,
          discount_type: discountType,
          discount_value: discountValue,
          total_before_discount: totalBeforeDiscount || (total + discountAmount),
        } : {}),
      };

      const printService = FirestorePrintService.getInstance();
      const results = await printService.printCashierReceipt(
        order.id,
        String(order.order_number || order.id),
        receiptData,
      );
      const ok = results.some((r: { success: boolean }) => r.success);
      results.forEach((r: { jobId: string; success: boolean; message: string }) => console.log('🧾 [DIRECT PRINT] Job:', r.jobId, r.success ? 'ok' : r.message));

      if (ok) {
        alert(t('orders.print_sent'));
      } else {
        alert(t('orders.print_queued_warning'));
      }
    } catch (err: any) {
      console.error('🧾 [DIRECT PRINT] Failed:', err);
      alert(t('orders.print_failed_generic') + ': ' + (err?.message || 'Unknown error'));
    }
  };

  const handlePrintReceipt = async (order: Order) => {
    try {
      console.log('🧾 [PRINT RECEIPT] Opening Saheeh invoice (ZATCA) for', order.order_number || order.id);
      const { openOrderInvoice } = await import('../../utils/printOrderInvoice');
      await openOrderInvoice(order as any);
    } catch (error) {
      console.error('❌ [PRINT RECEIPT] failed:', error);
      alert(t('orders.invoice_failed') + ': ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
      case 'ready':
        return 'success';
      case 'pending':
      case 'preparing':
        return 'warning';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return t('orders.status_pending');
      case 'confirmed':
        return t('orders.status_confirmed');
      case 'preparing':
        return t('orders.status_preparing');
      case 'ready':
        return t('orders.status_ready');
      case 'delivered':
        return t('orders.status_delivered');
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
      case 'ready':
        return <CheckCircle />;
      case 'pending':
      case 'preparing':
        return <Pending />;
      case 'delivered':
        return <CheckCircle />;
      case 'cancelled':
        return <Cancel />;
      default:
        return <Pending />;
    }
  };

  // === ORDER TYPE HELPERS (Liefersoft-style) ===
  const getOrderType = (order: Order): string => {
    const ot = (order as any).order_type || '';
    if (ot === 'delivery' || ot === 'Delivery' || ot === 'Lieferung') return 'delivery';
    if (ot === 'pickup' || ot === 'Pickup' || ot === 'takeaway' || ot === 'Takeaway' || ot === 'Abholung') return 'pickup';
    if (ot === 'dine_in' || ot === 'Dine In' || ot === 'restaurant' || ot === 'Restaurant' || ot === 'Im Restaurant') return 'dine_in';
    return 'dine_in'; // default
  };

  const getOrderTypeConfig = (type: string) => {
    switch (type) {
      case 'delivery':
        return {
          label: t('orders.type_delivery'),
          icon: <DeliveryDining sx={{ fontSize: 18 }} />,
          color: '#E65100',
          bgColor: '#FFF3E0',
          borderColor: '#FF9800',
        };
      case 'pickup':
        return {
          label: t('orders.type_pickup'),
          icon: <ShoppingBag sx={{ fontSize: 18 }} />,
          color: '#2E7D32',
          bgColor: '#E8F5E9',
          borderColor: '#4CAF50',
        };
      case 'dine_in':
      default:
        return {
          label: t('orders.type_dine_in'),
          icon: <TableRestaurant sx={{ fontSize: 18 }} />,
          color: '#1565C0',
          bgColor: '#E3F2FD',
          borderColor: '#2196F3',
        };
    }
  };

  // Order type counts for filter tabs
  const orderTypeCounts = React.useMemo(() => {
    const source = searchQuery ? filteredOrders : orders;
    return {
      all: source.length,
      delivery: source.filter(o => getOrderType(o) === 'delivery').length,
      pickup: source.filter(o => getOrderType(o) === 'pickup').length,
      dine_in: source.filter(o => getOrderType(o) === 'dine_in').length,
    };
  }, [orders, filteredOrders, searchQuery]);

  // Apply order type filter + time slot filter on top of search results
  const displayOrders = React.useMemo(() => {
    const source = searchQuery ? filteredOrders : orders;
    let rows = orderTypeFilter === 'all' ? [...source] : source.filter(o => getOrderType(o) === orderTypeFilter);
    // Sort newest first
    rows.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    // Time slot filter
    if (timeFilter) {
      const slot = TIME_SLOTS.find(s => s.value === timeFilter);
      if (slot) {
        rows = rows.filter(r => {
          const h = new Date(r.created_at).getHours();
          return slot.start < slot.end ? (h >= slot.start && h < slot.end) : (h >= slot.start || h < slot.end);
        });
      }
    }
    return rows;
  }, [orders, filteredOrders, searchQuery, orderTypeFilter, timeFilter]);

  const handleViewOrder = async (order: Order) => {
    console.log('👁️ Viewing order:', order.id);
    console.log('👁️ [ORDER DETAILS] Order data received:', {
      id: order.id,
      customer_name: order.customer_name,
      customer_first_name: (order as any).customer_first_name,
      customer_last_name: (order as any).customer_last_name,
      customer_phone: (order as any).customer_phone,
      customer_address: (order as any).customer_address,
      customer_postcode: (order as any).customer_postcode,
      customer_city: (order as any).customer_city,
      items_count: order.items?.length || 0,
      total: order.total
    });

    // CRITICAL: If customer detail fields are missing, fetch full order from Firestore
    if (!(order as any).customer_first_name && !(order as any).customer_last_name && !(order as any).customer_phone && order.customer_name && order.customer_name !== 'Walk-in Customer') {
      console.log('⚠️ [ORDER DETAILS] Customer detail fields missing, fetching full order from Firestore...');
      try {
        const { doc, getDoc } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');
        const orderDocRef = doc(db, 'orders', order.id);
        const orderDocSnap = await getDoc(orderDocRef);

        if (orderDocSnap.exists()) {
          const fullOrderData = orderDocSnap.data();
          console.log('✅ [ORDER DETAILS] Full order data from Firestore:', {
            customer_first_name: fullOrderData.customer_first_name,
            customer_last_name: fullOrderData.customer_last_name,
            customer_phone: fullOrderData.customer_phone,
            customer_address: fullOrderData.customer_address,
            customer_postcode: fullOrderData.customer_postcode,
            customer_city: fullOrderData.customer_city,
            items_count: fullOrderData.items?.length || 0
          });

          // Merge full order data with existing order
          const completeOrder = {
            ...order,
            customer_first_name: fullOrderData.customer_first_name || '',
            customer_last_name: fullOrderData.customer_last_name || '',
            customer_phone: fullOrderData.customer_phone || '',
            customer_address: fullOrderData.customer_address || '',
            customer_postcode: fullOrderData.customer_postcode || '',
            customer_city: fullOrderData.customer_city || '',
            // Ensure items array is properly formatted
            items: Array.isArray(fullOrderData.items) ? fullOrderData.items.map((item: any) => ({
              id: item.id || item.menu_item_id || '',
              name: item.name || item.menu_item?.name || 'Item',
              quantity: item.quantity || 1,
              price: parseFloat(String(item.price || item.unit_price || 0)),
              unit_price: parseFloat(String(item.unit_price || item.price || 0)),
              total: parseFloat(String(item.total || item.price || item.unit_price || 0)),
              notes: item.notes || '', // Include notes
              addons: item.addons || [], // Include addons
            })) : (order.items || []),
          };

          setSelectedOrder(completeOrder as Order);
          console.log('✅ [ORDER DETAILS] Complete order set with all customer fields');
        } else {
          console.warn('⚠️ [ORDER DETAILS] Order document not found in Firestore');
          setSelectedOrder(order);
        }
      } catch (error) {
        console.error('❌ [ORDER DETAILS] Error fetching full order data:', error);
        setSelectedOrder(order); // Fallback to original order
      }
    } else {
      // All fields are present, use order as-is
      setSelectedOrder(order);
      console.log('✅ [ORDER DETAILS] Order already has all customer fields');
    }

    setOpenViewDialog(true);
    setDriverLocation(null);
    console.log('✅ [ORDER DETAILS] Dialog opened with order data');

    // Fetch driver location if driver is assigned
    if ((order as any).rider_email || (order as any).assigned_driver_name) {
      try {
        const { collection, query, where, getDocs, doc, getDoc } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        let driverId: string | null = null;
        const riderEmail = (order as any).rider_email;

        if (riderEmail) {
          const driversRef = collection(db, 'drivers');
          const q = query(driversRef, where('email', '==', riderEmail));
          const driversSnapshot = await getDocs(q);

          if (!driversSnapshot.empty) {
            driverId = driversSnapshot.docs[0].id;
          }
        }

        if (driverId) {
          const locationDocRef = doc(db, 'driver_locations', driverId);
          const locationDocSnap = await getDoc(locationDocRef);

          if (locationDocSnap.exists()) {
            const locationData: any = locationDocSnap.data();
            setDriverLocation({
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              timestamp: locationData.timestamp?.toDate?.()?.toISOString() || locationData.timestamp,
              is_online: locationData.is_online || false,
            });
          } else {
            const driverDocRef = doc(db, 'drivers', driverId);
            const driverDocSnap = await getDoc(driverDocRef);

            if (driverDocSnap.exists()) {
              const driverData = driverDocSnap.data();
              if (driverData.current_latitude && driverData.current_longitude) {
                setDriverLocation({
                  latitude: driverData.current_latitude,
                  longitude: driverData.current_longitude,
                  is_online: driverData.is_available || false,
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching driver location:', error);
      }
    }
  };

  // Search and filter function
  const applySearchFilter = (ordersList: Order[], query: string, filter: 'all' | 'order_number' | 'customer' | 'price' | 'date', sort: 'newest' | 'oldest' | 'price_high' | 'price_low', dateFrom?: string, dateTo?: string) => {
    let filtered: Order[];

    // Handle date filter with date pickers
    if (filter === 'date' && (dateFrom || dateTo)) {
      filtered = ordersList.filter(order => {
        const orderDate = new Date(order.created_at);
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          if (orderDate < from) return false;
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          if (orderDate > to) return false;
        }
        return true;
      });
    } else if (!query.trim()) {
      filtered = [...ordersList];
    } else {
      const searchLower = query.toLowerCase().trim();
      filtered = ordersList.filter(order => {
        switch (filter) {
          case 'order_number':
            return (order.order_number || order.id).toLowerCase().includes(searchLower);
          case 'customer':
            return (order.customer_name || '').toLowerCase().includes(searchLower);
          case 'price':
            const priceStr = order.total.toFixed(2);
            return priceStr.includes(searchLower) || order.total.toString().includes(searchLower);
          case 'date':
            const orderDate = new Date(order.created_at);
            const dateStr = orderDate.toLocaleDateString('en-US');
            const timeStr = orderDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            return dateStr.includes(searchLower) || timeStr.includes(searchLower);
          case 'all':
          default:
            return (
              (order.order_number || order.id).toLowerCase().includes(searchLower) ||
              (order.customer_name || '').toLowerCase().includes(searchLower) ||
              order.total.toString().includes(searchLower) ||
              new Date(order.created_at).toLocaleDateString('en-US').includes(searchLower) ||
              new Date(order.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }).includes(searchLower)
            );
        }
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sort) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'price_high':
          return b.total - a.total;
        case 'price_low':
          return a.total - b.total;
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    setFilteredOrders(filtered);
  };

  // Handle search input change
  useEffect(() => {
    applySearchFilter(orders, searchQuery, searchFilter, sortBy, searchDateFrom, searchDateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchFilter, sortBy, searchDateFrom, searchDateTo, orders]);
  
  // Initialize filteredOrders when orders change
  useEffect(() => {
    if (orders.length > 0 && filteredOrders.length === 0 && !searchQuery) {
      setFilteredOrders(orders);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  // Apply discount to an existing order (POS-only).
  // Works for any order_type — dine-in, takeaway, delivery, webshop, QR.
  const openDiscountFor = (order: Order) => {
    setDiscountDialog({ open: true, order });
    const orderAny = order as any;
    setDiscountTypeInput(orderAny.discount_type === 'fixed' ? 'fixed' : 'percentage');
    setDiscountValueInput(orderAny.discount_value ? String(orderAny.discount_value) : '');
    setDiscountReasonInput(orderAny.discount_reason || '');
  };

  const handleApplyOrderDiscount = async () => {
    const order = discountDialog.order;
    if (!order) return;
    const val = parseFloat(discountValueInput);
    if (isNaN(val) || val <= 0) {
      alert('Enter a valid discount value');
      return;
    }
    setApplyingDiscount(true);
    try {
      const { supabase } = await import('../../services/supabaseClient');
      const orderAny = order as any;
      const originalTotal = orderAny.total_before_discount || order.total || 0;
      const discountAmount = discountTypeInput === 'percentage'
        ? Math.round((originalTotal * val / 100) * 100) / 100
        : Math.min(val, originalTotal);
      const newTotal = Math.round((originalTotal - discountAmount) * 100) / 100;
      const { error: discErr } = await supabase.from('orders').update({
        discount_type: discountTypeInput,
        discount_value: val,
        discount_total: discountAmount,
        discount_reason: discountReasonInput || (discountTypeInput === 'percentage' ? `${val}% discount` : `${val} EUR discount`),
        total_before_discount: originalTotal,
        grand_total: newTotal,
        updated_at: new Date().toISOString(),
      }).eq('id', order.id);
      if (discErr) throw discErr;
      setOrders(prev => prev.map(o => o.id === order.id ? ({
        ...o,
        total: newTotal,
        ...({ discount_type: discountTypeInput, discount_value: val, discount_amount: discountAmount, discount_reason: discountReasonInput, total_before_discount: originalTotal } as any),
      }) : o));
      setDiscountDialog({ open: false, order: null });
      setDiscountValueInput('');
      setDiscountReasonInput('');
    } catch (err) {
      console.error('🏷️ [DISCOUNT] Failed to apply:', err);
      alert('Failed to apply discount');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleRemoveOrderDiscount = async () => {
    const order = discountDialog.order;
    if (!order) return;
    setApplyingDiscount(true);
    try {
      const { supabase } = await import('../../services/supabaseClient');
      const orderAny = order as any;
      const restoreTotal = orderAny.total_before_discount || order.total || 0;
      const { error: remErr } = await supabase.from('orders').update({
        discount_type: null,
        discount_value: null,
        discount_total: 0,
        discount_reason: null,
        total_before_discount: null,
        grand_total: restoreTotal,
        updated_at: new Date().toISOString(),
      }).eq('id', order.id);
      if (remErr) throw remErr;
      setOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        const cleaned: any = { ...o, total: restoreTotal };
        delete cleaned.discount_type;
        delete cleaned.discount_value;
        delete cleaned.discount_amount;
        delete cleaned.discount_reason;
        delete cleaned.total_before_discount;
        return cleaned;
      }));
      setDiscountDialog({ open: false, order: null });
    } catch (err) {
      console.error('🏷️ [DISCOUNT] Failed to remove:', err);
      alert('Failed to remove discount');
    } finally {
      setApplyingDiscount(false);
    }
  };

  // Complete order function for dine-in/restaurant orders
  const handleCompleteOrder = async (orderId: string) => {
    try {
      console.log('✅ [ORDERS] Completing order:', orderId);
      
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        alert(t('orders.order_not_found'));
        return;
      }

      // Update order status to completed
      await handleUpdateOrderStatus(orderId, 'completed');
      
      console.log('✅ [ORDERS] Order completed successfully');
    } catch (error) {
      console.error('❌ [ORDERS] Failed to complete order:', error);
      setError(t('orders.complete_failed'));
    }
  };

  // Fix payment status for delivered/completed orders with pending payment
  const handleFixPaymentStatus = async (orderId: string) => {
    try {
      console.log('💳 [ORDERS] Fixing payment status for order:', orderId);
      
      const firebaseOrderService = (await import('../../services/firebaseOrderService')).default;
      const service = firebaseOrderService.getInstance();
      
      // Get current order to check status
      const order = orders.find(o => o.id === orderId);
      if (!order) {
        alert(t('orders.order_not_found'));
        return;
      }

      const currentStatus = order.status || 'pending';
      const currentPaymentStatus = (order as any).payment_status || (order as any).paymentStatus || 'pending';

      // Only fix if order is delivered/completed but payment is pending
      const isDeliveredOrCompleted = currentStatus === 'delivered' || (currentStatus as string) === 'completed';
      if (isDeliveredOrCompleted && currentPaymentStatus !== 'completed') {
        // Update order status again to trigger payment fix
        await service.updateOrderStatus(orderId, currentStatus as any);
        alert('✅ ' + t('orders.payment_fixed'));

        // Refresh the page to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        alert(t('orders.payment_fix_condition'));
      }
    } catch (error: any) {
      console.error('❌ [ORDERS] Failed to fix payment status:', error);
      alert(t('orders.payment_fix_failed') + ': ' + (error.message || 'Please try again.'));
    }
  };

  // Update order status in Firebase
  const handleUpdateOrderStatus = async (orderId: string, newStatus: string, managerEmail?: string, managerReason?: string) => {
    try {
      console.log('📋 [ORDERS] Updating order status:', orderId, 'to', newStatus, managerEmail ? `(approved by ${managerEmail})` : '');

      // FRAUD PREVENTION #2: Manager PIN required for cancellations
      if (newStatus === 'cancelled' && !managerEmail) {
        const orderForCancel = orders.find(o => o.id === orderId);
        const orderNum = (orderForCancel as any)?.order_number || orderId.substring(0, 8);
        const orderTotal = (orderForCancel as any)?.total || 0;
        setManagerPinAction({
          label: `Cancel order #${orderNum} (€${orderTotal.toFixed(2)})`,
          requireReason: true,
          onApproved: (mEmail, reason) => handleUpdateOrderStatus(orderId, newStatus, mEmail, reason),
        });
        setManagerPinOpen(true);
        return;
      }

      // Find the order to check if driver is assigned
      const order = orders.find(o => o.id === orderId);
      if (order && (order as any).rider_email) {
        // Driver is assigned - allow cancellation and manual completion by POS staff
        const allowedStatuses = ['cancelled', 'completed'];
        if (!allowedStatuses.includes(newStatus)) {
          console.warn('⚠️ [ORDERS] Cannot change status to', newStatus, '- driver is managing this delivery.');
          alert(t('orders.driver_manages_status'));
          return;
        }
      }

      // Write the status change to Supabase. Manager-approval metadata
      // goes into the same row in one update so the audit chain stays
      // single-entry (was originally a Firestore audit-trigger concern;
      // Supabase doesn't have a trigger yet, but the design stays).
      {
        const { supabase } = await import('../../services/supabaseClient');
        const patch: Record<string, any> = {
          status: newStatus,
          updated_at: new Date().toISOString(),
        };
        if (managerEmail) {
          patch.manager_pin_used = true;
          patch.last_modified_by = managerEmail;
          patch.last_modified_by_role = 'manager';
          patch.last_modification_reason = managerReason || '';
        }
        // Stamp lifecycle timestamps so the audit trail is meaningful.
        if (newStatus === 'confirmed') patch.confirmed_at = new Date().toISOString();
        if (newStatus === 'ready')     patch.ready_at     = new Date().toISOString();
        if (newStatus === 'delivered') patch.delivered_at = new Date().toISOString();
        if (newStatus === 'completed') patch.completed_at = new Date().toISOString();
        if (newStatus === 'cancelled') patch.cancelled_at = new Date().toISOString();

        const { error } = await supabase.from('orders').update(patch).eq('id', orderId);
        if (error) throw error;
      }

      // Update local state immediately (Firestore real-time listener will also update)
      const updatedOrders = orders.map(o =>
        o.id === orderId ? { ...o, status: newStatus as any } : o
      );
      setOrders(updatedOrders);

      // If dine-in order is completed/cancelled, check if table should be freed
      if ((newStatus === 'completed' || newStatus === 'cancelled') && order) {
        const orderType = (order as any).order_type || '';
        const tableNumber = (order as any).table_number;
        if ((orderType === 'dine_in' || orderType === 'restaurant') && tableNumber) {
          // Check if there are remaining active orders on this table
          const remainingActive = updatedOrders.filter(o =>
            (o as any).table_number === tableNumber &&
            o.id !== orderId &&
            o.status !== 'completed' && o.status !== 'cancelled'
          );
          if (remainingActive.length === 0) {
            // No active orders left — free the table
            try {
              const { firebaseTableService } = await import('../../services/firebaseTableService');
              await firebaseTableService.updateTableByNumber(
                tableNumber, 'available',
                { customer_name: undefined, start_time: undefined, active_order_number: undefined },
                undefined // area unknown, service will find correct doc
              );
              console.log(`✅ [ORDERS] Table ${tableNumber} freed — no active orders remaining`);
            } catch (err) {
              console.warn(`⚠️ [ORDERS] Could not free table ${tableNumber}:`, err);
            }
          } else {
            console.log(`ℹ️ [ORDERS] Table ${tableNumber} still has ${remainingActive.length} active order(s)`);
          }
        }
      }

      console.log('✅ [ORDERS] Order status updated successfully');
      // Visible confirmation so staff knows the cancel actually went through.
      // Without this, after the PIN dialog closes the order row changes color
      // silently and it's easy to think nothing happened.
      if (newStatus === 'cancelled') {
        const orderNum = orders.find(o => o.id === orderId)?.order_number || orderId.substring(0, 8);
        window.setTimeout(() => {
          try {
            // Prefer a lightweight toast via setError with success class would be overkill —
            // a simple alert is plenty for a critical destructive action.
            alert(`✅ Order #${orderNum} cancelled${managerEmail ? ' (approved by ' + managerEmail + ')' : ''}`);
          } catch {}
        }, 50);
      }
    } catch (error: any) {
      console.error('❌ [ORDERS] Failed to update order status:', error);
      // Previously only set an inline banner. Staff was missing it, especially
      // after the PIN dialog auto-closes. Alert for clarity on failure too.
      const msg = error?.message || t('orders.update_failed');
      setError(msg);
      alert(`❌ Cancel failed: ${msg}`);
    }
  };

  const handleAssignDelivery = (order: Order) => {
    console.log('🚗 [DRIVER ASSIGNMENT] ========================================');
    console.log('🚗 [DRIVER ASSIGNMENT] STEP 2: Opening driver assignment dialog');
    console.log('🚗 [DRIVER ASSIGNMENT] Order details:', {
      order_id: order.id,
      order_number: order.order_number || 'N/A',
      customer: order.customer_name || 'N/A',
      status: order.status,
      total: order.total,
      rider_email: (order as any).rider_email || 'NOT ASSIGNED',
      current_assigned_driver: (order as any).assigned_driver_name || 'NOT SET',
    });
    console.log('🚗 [DRIVER ASSIGNMENT] Available drivers count:', drivers.length);
    console.log('🚗 [DRIVER ASSIGNMENT] ========================================');

    setSelectedOrder(order);
    setOpenDeliveryDialog(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedOrder || !selectedDriver) {
      console.warn('⚠️ [DRIVER ASSIGNMENT] Missing order or driver selection');
      alert(t('orders.select_driver_first'));
      return;
    }

    try {
      console.log('🚗 [DRIVER ASSIGNMENT] ========================================');
      console.log('🚗 [DRIVER ASSIGNMENT] STEP 3: Starting driver assignment');

      const selectedDriverData = drivers.find(d => d.id === selectedDriver);
      if (!selectedDriverData) {
        console.error('❌ [DRIVER ASSIGNMENT] Driver not found in list:', selectedDriver);
        alert(t('orders.driver_not_found'));
        return;
      }

      console.log('🚗 [DRIVER ASSIGNMENT] Selected driver:', {
        id: selectedDriverData.id,
        name: selectedDriverData.name,
        email: selectedDriverData.email,
        phone: selectedDriverData.phone,
        current_status: selectedDriverData.status,
        current_available: selectedDriverData.is_available,
      });

      console.log('🚗 [DRIVER ASSIGNMENT] Target order:', {
        id: selectedOrder.id,
        order_number: selectedOrder.order_number || 'N/A',
        current_status: selectedOrder.status,
        current_rider_email: (selectedOrder as any).rider_email || 'NOT SET',
      });

      // Update order in Firestore with assigned driver
      const { doc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      console.log('🚗 [DRIVER ASSIGNMENT] STEP 4: Updating order in Firestore...');
      const orderRef = doc(db, 'orders', selectedOrder.id);

      const orderUpdateData = {
        // Driver assignment fields (for POS/Admin tracking)
        assigned_driver_id: selectedDriver,
        assigned_driver_name: selectedDriverData.name,
        assigned_driver_email: selectedDriverData.email,
        assigned_driver_phone: selectedDriverData.phone,
        // Rider email (for rider app to find assigned orders)
        rider_email: selectedDriverData.email,
        // Order status
        status: 'assigned', // Change to 'assigned' so rider can see it
        // Timestamps
        assigned_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      console.log('🚗 [DRIVER ASSIGNMENT] Order update data:', {
        ...orderUpdateData,
        assigned_at: '[serverTimestamp]',
        updated_at: '[serverTimestamp]',
      });

      await updateDoc(orderRef, orderUpdateData);
      console.log('✅ [DRIVER ASSIGNMENT] STEP 4 COMPLETE: Order updated in Firestore');
      console.log('🚗 [DRIVER ASSIGNMENT] Key fields set:', {
        rider_email: selectedDriverData.email,
        assigned_driver_name: selectedDriverData.name,
        status: 'assigned',
      });

      // Update driver status to busy (optional - can be done automatically)
      console.log('🚗 [DRIVER ASSIGNMENT] STEP 5: Updating driver status to busy...');
      try {
        const driverRef = doc(db, 'drivers', selectedDriver);
        const driverUpdateData = {
          status: 'busy',
          is_available: false,
          updated_at: serverTimestamp(),
        };
        console.log('🚗 [DRIVER ASSIGNMENT] Driver update data:', {
          ...driverUpdateData,
          updated_at: '[serverTimestamp]',
        });

        await updateDoc(driverRef, driverUpdateData);
        console.log(`✅ [DRIVER ASSIGNMENT] STEP 5 COMPLETE: Driver ${selectedDriverData.name} status updated to busy`);
      } catch (driverUpdateError) {
        console.warn('⚠️ [DRIVER ASSIGNMENT] STEP 5 WARNING: Failed to update driver status (non-critical):', driverUpdateError);
      }

      // Update local state immediately for instant feedback
      setOrders(orders.map(order =>
        order.id === selectedOrder.id
          ? {
            ...order,
            assigned_driver: selectedDriverData.name,
            assigned_driver_id: selectedDriver,
            assigned_driver_email: selectedDriverData.email,
            rider_email: selectedDriverData.email,
            assigned_driver_name: selectedDriverData.name,
            assigned_driver_phone: selectedDriverData.phone,
            status: 'assigned' as any  // Changed from 'confirmed' to 'assigned'
          }
          : order
      ));

      // Show success notification
      alert('✅ ' + t('orders.driver_assigned') + ': ' + selectedDriverData.name + ' → ' + (selectedOrder.order_number || selectedOrder.id));

      // Close modal and reset selection
      setOpenDeliveryDialog(false);
      setSelectedDriver(null);
      setSelectedOrder(null);

      // Refresh drivers to update their availability
      const { collection, query, where, getDocs } = await import('../../services/firestoreCompat');
      const driversRef = collection(db, 'drivers');
      // Query without orderBy to avoid index requirement - we'll sort in JavaScript
      const q = query(
        driversRef,
        where('is_active', '==', true)
      );
      const snapshot = await getDocs(q);
      const driversList: any[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        driversList.push({
          id: doc.id,
          name: data.name || data.email || 'Unknown Driver',
          email: data.email || '',
          phone: data.phone || '',
          status: data.status || 'offline',
          // CRITICAL: Default to false (offline) if field is missing - rider must explicitly toggle online
          is_available: data.is_available === true,
          vehicle_type: data.vehicle_type || 'bicycle',
          rating: data.rating || 5.0,
          deliveries_today: data.deliveries_today || 0,
        });
      });

      // Sort drivers by name in JavaScript (no index required)
      driversList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setDrivers(driversList);

      console.log('🚗 [DRIVER ASSIGNMENT] STEP 6: Driver list refreshed');
      console.log(`✅ [DRIVER ASSIGNMENT] ASSIGNMENT COMPLETE!`);
      console.log('🚗 [DRIVER ASSIGNMENT] Summary:', {
        order_id: selectedOrder.id,
        driver_name: selectedDriverData.name,
        driver_email: selectedDriverData.email,
        rider_email_set: selectedDriverData.email,
        order_status: 'assigned',
        driver_status: 'busy',
      });
      console.log('🚗 [DRIVER ASSIGNMENT] Next: Rider should see this order in "My Orders"');
      console.log('🚗 [DRIVER ASSIGNMENT] Query: rider_email == "' + selectedDriverData.email + '"');
      console.log('🚗 [DRIVER ASSIGNMENT] ========================================');

    } catch (err: any) {
      console.error('❌ [DRIVER ASSIGNMENT] ASSIGNMENT FAILED:', err);
      console.error('❌ [DRIVER ASSIGNMENT] Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack,
      });
      alert(t('orders.assign_driver_failed') + ': ' + (err.message || 'Unknown error'));
    }
  };

  // Handle editing an order item
  const handleEditItem = (item: any, index: number) => {
    setEditingItem({ item, index });
    setOpenEditItemModal(true);
  };

  // Handle saving edited item
  const handleSaveEditedItem = async (updatedItem: any, itemIndex: number) => {
    if (!selectedOrder) return;

    try {
      console.log('📝 [EDIT ITEM] Saving edited item:', { itemIndex, updatedItem });

      // Import Firebase services
      const { doc, updateDoc, serverTimestamp } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');

      // Get the order document reference
      const orderRef = doc(db, 'orders', selectedOrder.id);

      // Get current order data
      const currentItems = [...(selectedOrder.items || [])];
      
      // Update the specific item
      currentItems[itemIndex] = {
        ...currentItems[itemIndex],
        ...updatedItem,
        // Ensure addons is an array
        addons: Array.isArray(updatedItem.addons) ? updatedItem.addons : (updatedItem.addons ? [updatedItem.addons] : []),
      };

      // Recalculate order totals
      let newSubtotal = 0;
      let newTax = 0;
      let newTotal = 0;

      currentItems.forEach((item: any) => {
        const itemSubtotal = (item.unit_price || item.price || 0) * (item.quantity || 1);
        const itemTaxRate = item.tax_rate || 7;
        const itemTax = itemSubtotal * (itemTaxRate / 100);
        const itemTotal = itemSubtotal + itemTax;

        // Add addon prices
        if (item.addons && Array.isArray(item.addons)) {
          // If addons are strings (IDs), we need to fetch their prices
          // For now, we'll assume addons are stored with prices in the item
          // This is a simplified version - you may need to fetch addon prices from Firebase
          const addonPrices = (item.addon_prices || []).reduce((sum: number, price: number) => sum + price, 0);
          newTotal += itemTotal + addonPrices;
        } else {
          newTotal += itemTotal;
        }

        newSubtotal += itemSubtotal;
        newTax += itemTax;
      });

      // Update the order in Firebase
      await updateDoc(orderRef, {
        items: currentItems,
        subtotal: newSubtotal,
        tax: newTax,
        total: newTotal,
        updated_at: serverTimestamp(),
      });

      // Update local state
      const updatedOrder = {
        ...selectedOrder,
        items: currentItems,
        subtotal: newSubtotal,
        tax: newTax,
        total: newTotal,
      };
      setSelectedOrder(updatedOrder);

      // Update orders list
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrder.id ? updatedOrder : order
        )
      );

      console.log('✅ [EDIT ITEM] Item updated successfully');
    } catch (error: any) {
      console.error('❌ [EDIT ITEM] Error updating item:', error);
      throw error;
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'delivered' | 'cancelled' | 'completed') => {
    try {
      console.log(`📋 [ORDERS] Updating order status: ${orderId} to ${newStatus}`);

      // Use Firebase SDK instead of Django API
      const firebaseOrderService = (await import('../../services/firebaseOrderService')).default;
      const service = firebaseOrderService.getInstance();

      // Update order status via Firebase
      await service.updateOrderStatus(orderId, newStatus);

      // Update local state
      setOrders(orders.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Show success notification
      console.log(`✅ [ORDERS] Order status updated successfully`);
      // Don't use alert - use a toast notification if available, or just log

      setOpenViewDialog(false);
    } catch (err: any) {
      console.error('❌ Failed to update order status:', err);
      alert(t('orders.update_failed') + ': ' + (err.message || ''));
    }
  };

  const handlePrintPreview = (order: Order) => {
    // Print Preview now shows the same full invoice as "Print Invoice" (eye icon)
    handlePrintReceipt(order);
  };

  const handlePrintOrder = (order: Order) => {
    try {
      console.log('🧾 [PRINT ORDER] Printing order for:', order.order_number || order.id);
      console.log('🧾 [PRINT ORDER] Order data:', {
        id: order.id,
        order_number: order.order_number,
        customer_name: order.customer_name,
        total: order.total,
        items_count: order.items?.length || 0
      });

      if (!order) {
        console.error('❌ [PRINT ORDER] No order provided');
        alert(t('orders.no_order_selected'));
        return;
      }

      // Use the print service for professional printing
      try {
        printReportService.printOrderReceipt(order);
        console.log('✅ [PRINT ORDER] Print service called successfully');
        return; // Exit early if print service succeeds
      } catch (printServiceError) {
        console.error('❌ [PRINT ORDER] Print service error:', printServiceError);
        console.log('🔄 [PRINT ORDER] Falling back to browser print...');
        // Fallback to browser print
        handlePrintPreview(order);
        return;
      }
    } catch (error) {
      console.error('❌ [PRINT ORDER] Error in handlePrintOrder:', error);
      alert(t('orders.print_failed') + ': ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Debug logging
  console.log('Component render - openViewDialog:', openViewDialog, 'openDeliveryDialog:', openDeliveryDialog, 'selectedOrder:', selectedOrder);

  return (
    <Box sx={{
      display: hasSharedSidebar ? 'block' : 'flex',
      width: '100%',
      minHeight: '100vh',
      backgroundColor: appTheme.theme.background_color || '#1a1d2e'
    }}>
      {/* Sidebar - Only show if NOT in SharedLayout */}
      {!hasSharedSidebar && (
        <Box sx={{
          width: 280,
          backgroundColor: appTheme.theme.background_color || '#1a1d2e',
          borderRight: '1px solid #3a3d52',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative',
          height: '100vh'
        }}>

          {/* Logo Section */}
          <Box sx={{ p: 3, borderBottom: '1px solid #3a3d52' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 180,
                height: 180,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                <img
                  src="/saheeh_logo_w.svg"
                  alt="Saheeh"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#e0e0e0' }}>
                  POS
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  Berlin Mitte
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Navigation */}
          <Box sx={{ flex: 1, p: 2, paddingBottom: '80px' }}>
            {[
              { label: t('nav.dashboard'), icon: <Dashboard />, path: '/dashboard', active: false },
              { label: t('nav.orders'), icon: <Restaurant />, path: '/orders', active: true },
              { label: t('nav.payments'), icon: <Euro />, path: '/payments', active: false },
              { label: t('nav.reports'), icon: <TrendingUp />, path: '/reports', active: false },
            ].map((item) => (
              <Button
                key={item.label}
                fullWidth
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  justifyContent: 'flex-start',
                  p: 2,
                  mb: 1,
                  borderRadius: '8px',
                  backgroundColor: item.active ? '#FF8E53' : '#2d3048',
                  color: item.active ? '#ffffff' : '#9ca3af',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: item.active ? 600 : 500,
                  minWidth: '100%',
                  border: item.active ? 'none' : '1px solid #3a3d52',
                  boxShadow: item.active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: item.active ? '#FF8E53' : '#3a3d52',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Logout */}
          <Box sx={{
            p: 2,
            borderTop: '1px solid #3a3d52',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: appTheme.theme.background_color || '#1a1d2e',
            zIndex: 10
          }}>
            <Button
              fullWidth
              startIcon={<ExitToApp />}
              onClick={() => navigate('/login')}
              sx={{
                justifyContent: 'flex-start',
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#2d3048',
                color: '#9ca3af',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 500,
                minWidth: '100%',
                border: '1px solid #3a3d52',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#2d3048',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                },
              }}
            >
              {t('nav.logout')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: hasSharedSidebar ? undefined : 1, width: hasSharedSidebar ? '100%' : 'auto', display: 'flex', flexDirection: 'column', backgroundColor: '#252838', overflow: { xs: 'auto', sm: 'hidden' }, height: { xs: 'auto', sm: '100vh' }, minHeight: { xs: 'calc(100vh - 56px)' }, WebkitOverflowScrolling: 'touch' }} className="main-content-area">
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: { xs: 'auto', sm: '100%' }, overflow: { xs: 'visible', sm: 'hidden' } }}>
          {/* Header - sticky on mobile */}
          <Box sx={{
            p: { xs: 1.5, md: 3 }, pb: { xs: 1, md: 2 },
            position: { xs: 'sticky', sm: 'relative' },
            top: { xs: 0 },
            zIndex: 10,
            background: { xs: '#252838', md: 'linear-gradient(180deg, #1e2030 0%, #252838 100%)' },
            borderBottom: { xs: '1px solid #3a3d52', sm: 'none' },
          }}>
          {/* Title bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'center', md: 'flex-end' }, mb: { xs: 1, md: 2.5 }, flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography sx={{
                fontWeight: 800, color: '#fff',
                fontSize: { xs: '1.25rem', md: '1.85rem' },
                letterSpacing: '-0.4px', lineHeight: 1.1,
              }}>
                {t('orders.title')}
              </Typography>
              <Typography sx={{
                color: '#8a93b0', fontSize: { xs: '0.72rem', md: '0.82rem' },
                fontWeight: 500, mt: 0.25, display: { xs: 'none', md: 'block' },
              }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1,
                px: 1.5, py: 0.75, borderRadius: '10px',
                background: 'linear-gradient(135deg, rgba(0, 200, 150,0.08) 0%, rgba(59,130,246,0.08) 100%)',
                border: '1px solid rgba(0, 200, 150,0.22)',
              }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00C896 0%, #3b82f6 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 200, 150,0.25)',
                }}>
                  <Person sx={{ fontSize: 16, color: '#0f1117' }} />
                </Box>
                <Box sx={{ lineHeight: 1.1 }}>
                  {(() => {
                    const u = getCurrentUser();
                    const r = u.role;
                    return (
                      <>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#e8eaf6', lineHeight: 1.1 }}>
                          {u.name || u.email || 'Unknown'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#00C896', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              </Box>
              <IconButton
                onClick={handleRefresh}
                sx={{
                  display: { xs: 'flex', md: 'none' }, color: '#9ca3af',
                  border: '1px solid #3a3d52', borderRadius: '10px', width: 38, height: 38,
                  '&:hover': { borderColor: '#00C896', color: '#00C896' },
                }}
              >
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={handleRefresh}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                  backgroundColor: 'rgba(0, 200, 150,0.12)',
                  color: '#00C896',
                  border: '1px solid rgba(0, 200, 150,0.3)',
                  boxShadow: 'none',
                  px: 2, height: 40,
                  display: { xs: 'none', md: 'inline-flex' },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 200, 150,0.2)',
                    borderColor: '#00C896',
                    boxShadow: '0 4px 14px rgba(0, 200, 150,0.25)',
                  },
                }}
              >
                {t('orders.refresh_orders')}
              </Button>
            </Box>
          </Box>

          {/* Premium KPI Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 0.75, md: 2 }, mb: { xs: 1, md: 2.5 } }}>
            {(() => {
              // Single source of truth: shared reportingUtils filters.
              // Active = in-prep pipeline (incl. assigned/in_transit for delivery).
              // Completed = terminal positive states.
              // Revenue = isRevenueOrder() — same filter Admin Reports + Branches use,
              // so all three pages reconcile to the cent.
              const nonCancelledOrders = orders.filter(o => (o.status || '').toLowerCase() !== 'cancellation');
              const activeCount = orders.filter(isActiveOrder).length;
              const completedCount = orders
                .filter(o => REVENUE_TERMINAL_STATUSES.has((o.status || '').toLowerCase()))
                .length;
              const revenue = orders
                .filter(isRevenueOrder)
                .reduce((sum, o) => sum + (o.total || 0), 0);
              const avgTicket = completedCount > 0 ? revenue / completedCount : 0;
              return [
                {
                  label: t('orders.stats_total'),
                  value: nonCancelledOrders.length,
                  sub: `Ø ${money.fmt(avgTicket)}`,
                  icon: <Receipt sx={{ fontSize: { xs: 16, md: 20 } }} />,
                  accent: '#3b82f6',
                  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                },
                {
                  label: t('orders.stats_active'),
                  value: activeCount,
                  sub: activeCount > 0 ? 'In progress' : 'All clear',
                  icon: <Pending sx={{ fontSize: { xs: 16, md: 20 } }} />,
                  accent: '#f59e0b',
                  gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                },
                {
                  label: t('orders.stats_completed'),
                  value: completedCount,
                  sub: nonCancelledOrders.length > 0 ? `${Math.round((completedCount / nonCancelledOrders.length) * 100)}%` : '0%',
                  icon: <CheckCircle sx={{ fontSize: { xs: 16, md: 20 } }} />,
                  accent: '#00C896',
                  gradient: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)',
                },
                {
                  label: t('orders.stats_revenue'),
                  value: money.fmt(revenue),
                  sub: 'Net sales',
                  icon: <Euro sx={{ fontSize: { xs: 16, md: 20 } }} />,
                  accent: '#a78bfa',
                  gradient: 'linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)',
                },
              ];
            })().map((stat) => (
              <Box key={stat.label} sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 1, md: 2 },
                borderRadius: { xs: '10px', md: '14px' },
                background: 'linear-gradient(145deg, #1e2139 0%, #252838 100%)',
                border: '1px solid rgba(255,255,255,0.05)',
                transition: 'all 0.2s ease',
                cursor: 'default',
                '&:hover': {
                  transform: { md: 'translateY(-2px)' },
                  borderColor: `${stat.accent}40`,
                  boxShadow: `0 10px 30px -10px ${stat.accent}40`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, height: '3px',
                  background: stat.gradient,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -30, right: -30, width: 90, height: 90,
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${stat.accent}15 0%, transparent 70%)`,
                  pointerEvents: 'none',
                },
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 0.5, md: 1 }, position: 'relative' }}>
                  <Box sx={{
                    width: { xs: 26, md: 34 }, height: { xs: 26, md: 34 },
                    borderRadius: '8px',
                    background: stat.gradient,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#0f1117',
                    boxShadow: `0 4px 12px ${stat.accent}30`,
                  }}>
                    {stat.icon}
                  </Box>
                  <Typography sx={{
                    fontSize: { xs: '0.55rem', md: '0.68rem' },
                    fontWeight: 600, color: stat.accent,
                    backgroundColor: `${stat.accent}15`,
                    px: 0.75, py: 0.25, borderRadius: '6px',
                    display: { xs: 'none', md: 'inline-block' },
                    textTransform: 'uppercase', letterSpacing: '0.3px',
                  }}>
                    {stat.sub}
                  </Typography>
                </Box>
                <Typography sx={{
                  fontWeight: 800, color: '#fff',
                  fontSize: { xs: '1.15rem', md: '1.75rem' },
                  lineHeight: 1.1, letterSpacing: '-0.5px',
                }}>
                  {stat.value}
                </Typography>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.55)',
                  fontWeight: 600, fontSize: { xs: '0.58rem', md: '0.72rem' },
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  mt: 0.25,
                }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Smart Toolbar: Search + Date Pills */}
          <Box sx={{
            display: 'flex', gap: { xs: 0.75, md: 1.25 }, alignItems: 'center',
            flexWrap: 'wrap',
            mb: { xs: 1, md: 1.5 },
            p: { xs: 0.75, md: 1 },
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.04)',
          }}>
            <TextField
              placeholder={t('orders.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{
                flex: { xs: '1 1 100%', md: '1 1 280px' },
                maxWidth: { md: '360px' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px', height: 40,
                  backgroundColor: '#1a1d29',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                  '&:hover fieldset': { borderColor: 'rgba(0, 200, 150,0.3)' },
                  '&.Mui-focused fieldset': { borderColor: '#00C896', borderWidth: 1 },
                },
                '& input': { color: '#e8eaf6', fontSize: '0.88rem' },
                '& input::placeholder': { color: '#6b7280', opacity: 1 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00C896', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: '#6b7280', p: 0.5, '&:hover': { color: '#f44336' } }}>
                      <Cancel sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Box sx={{
              display: 'flex', gap: 0.5, alignItems: 'center',
              p: 0.4, borderRadius: '10px',
              backgroundColor: '#1a1d29',
              border: '1px solid rgba(255,255,255,0.04)',
              flexWrap: 'wrap',
            }}>
              {([
                { key: 'today' as const, label: 'Today' },
                { key: 'yesterday' as const, label: 'Yesterday' },
                { key: '7days' as const, label: '7 Days' },
                { key: '30days' as const, label: '30 Days' },
                { key: 'custom' as const, label: 'Custom' },
              ]).map((opt) => (
                <Button
                  key={opt.key}
                  size="small"
                  disableRipple
                  onClick={() => setDateRange(opt.key)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: dateRange === opt.key ? 700 : 500,
                    fontSize: '0.78rem',
                    borderRadius: '8px',
                    px: 1.5, py: 0.6, minWidth: 'auto', minHeight: 32,
                    transition: 'all 0.15s ease',
                    background: dateRange === opt.key ? 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)' : 'transparent',
                    color: dateRange === opt.key ? '#0f1117' : '#8a93b0',
                    boxShadow: dateRange === opt.key ? '0 2px 8px rgba(0, 200, 150,0.3)' : 'none',
                    '&:hover': {
                      backgroundColor: dateRange === opt.key ? undefined : 'rgba(255,255,255,0.05)',
                      color: dateRange === opt.key ? '#0f1117' : '#e8eaf6',
                    },
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </Box>

            {dateRange === 'custom' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TextField
                  type="date"
                  size="small"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: '0.82rem', padding: '6px 10px', colorScheme: 'dark' } }}
                  sx={{
                    width: 150,
                    '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 40, backgroundColor: '#1a1d29' },
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                    '& input': { color: '#e8eaf6' },
                  }}
                />
                <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: 1 }}>→</Typography>
                <TextField
                  type="date"
                  size="small"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: '0.82rem', padding: '6px 10px', colorScheme: 'dark' } }}
                  sx={{
                    width: 150,
                    '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 40, backgroundColor: '#1a1d29' },
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                    '& input': { color: '#e8eaf6' },
                  }}
                />
              </Box>
            )}

            {/* Day navigation — prev/next arrows + Today shortcut */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small"
                onClick={() => {
                  setDateRange('custom');
                  const base = customDateFrom ? new Date(customDateFrom + 'T00:00:00') : getBusinessDayStart();
                  base.setDate(base.getDate() - 1);
                  setCustomDateFrom(base.toISOString().slice(0, 10));
                  setCustomDateTo(base.toISOString().slice(0, 10));
                }}
                sx={{ color: '#94a3b8', border: '1px solid #3a3d52', borderRadius: '8px', p: 0.7 }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <IconButton size="small"
                onClick={() => {
                  setDateRange('custom');
                  const base = customDateFrom ? new Date(customDateFrom + 'T00:00:00') : getBusinessDayStart();
                  base.setDate(base.getDate() + 1);
                  setCustomDateFrom(base.toISOString().slice(0, 10));
                  setCustomDateTo(base.toISOString().slice(0, 10));
                }}
                sx={{ color: '#94a3b8', border: '1px solid #3a3d52', borderRadius: '8px', p: 0.7 }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
            <Button
              onClick={() => { setDateRange('today'); setCustomDateFrom(''); setCustomDateTo(''); }}
              sx={{ backgroundColor: '#1e2139', color: '#94a3b8', border: '1px solid #3a3d52', borderRadius: '10px', px: 1.5, py: 1.3, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', '&:hover': { backgroundColor: '#252838' } }}>
              {isAr ? 'اليوم' : 'Today'}
            </Button>
          </Box>

          {/* Order Type Filter Pills */}
          <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, mb: { xs: 0.75, md: 1.5 }, alignItems: 'center', overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', '&::-webkit-scrollbar': { height: 0 }, flexShrink: 0 }}>
            {([
              { key: 'all' as const, label: t('orders.filter_all'), icon: <FilterList sx={{ fontSize: 16 }} />, color: '#00C896', gradient: 'linear-gradient(135deg, #00C896 0%, #65a30d 100%)' },
              { key: 'delivery' as const, label: t('orders.type_delivery'), icon: <DeliveryDining sx={{ fontSize: 16 }} />, color: '#f97316', gradient: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)' },
              { key: 'pickup' as const, label: t('orders.type_pickup'), icon: <ShoppingBag sx={{ fontSize: 16 }} />, color: '#22c55e', gradient: 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)' },
              { key: 'dine_in' as const, label: t('orders.type_dine_in'), icon: <TableRestaurant sx={{ fontSize: 16 }} />, color: '#3b82f6', gradient: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)' },
            ]).map((tab) => {
              const active = orderTypeFilter === tab.key;
              return (
                <Button
                  key={tab.key}
                  startIcon={tab.icon}
                  onClick={() => setOrderTypeFilter(tab.key)}
                  disableRipple
                  sx={{
                    textTransform: 'none',
                    fontWeight: active ? 700 : 600,
                    fontSize: { xs: '0.78rem', md: '0.88rem' },
                    borderRadius: '10px',
                    px: { xs: 1.5, md: 2 }, py: { xs: 0.75, md: 1 },
                    flexShrink: 0, whiteSpace: 'nowrap',
                    transition: 'all 0.18s ease',
                    background: active ? tab.gradient : 'rgba(255,255,255,0.02)',
                    color: active ? '#0f1117' : '#8a93b0',
                    border: active ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    boxShadow: active ? `0 4px 16px ${tab.color}30` : 'none',
                    '& .MuiButton-startIcon': { color: active ? '#0f1117' : tab.color, mr: 0.75 },
                    '&:hover': {
                      background: active ? tab.gradient : 'rgba(255,255,255,0.05)',
                      color: active ? '#0f1117' : '#fff',
                      transform: 'translateY(-1px)',
                      borderColor: active ? 'transparent' : `${tab.color}60`,
                    },
                  }}
                >
                  {tab.label}
                  <Box component="span" sx={{
                    ml: 1, minWidth: 24, height: 22,
                    px: 0.75, borderRadius: '6px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 800,
                    backgroundColor: active ? 'rgba(15,17,23,0.2)' : `${tab.color}20`,
                    color: active ? '#0f1117' : tab.color,
                  }}>
                    {orderTypeCounts[tab.key]}
                  </Box>
                </Button>
              );
            })}

            {/* Time slot filter */}
            <FormControl sx={{ minWidth: 150, ml: 'auto' }} size="small">
              <Select
                displayEmpty
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                renderValue={(v) => v
                  ? (TIME_SLOTS.find(s => s.value === v)?.[isAr ? 'labelAr' : 'label'] || v)
                  : <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>{isAr ? 'الوقت' : 'Time slot'}</Typography>
                }
                sx={{
                  borderRadius: '10px', height: 40,
                  backgroundColor: '#1a1d29',
                  color: '#e8eaf6', fontSize: '0.85rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.06)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,200,150,0.3)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C896' },
                  '& .MuiSvgIcon-root': { color: '#6b7280' },
                }}
              >
                <MenuItem value="">{isAr ? 'الكل' : 'All'}</MenuItem>
                {TIME_SLOTS.map(s => (
                  <MenuItem key={s.value} value={s.value}>{isAr ? s.labelAr : s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          </Box>
          {/* Scrollable content area */}
          <Box sx={{ flex: 1, px: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, overflow: { xs: 'visible', sm: 'hidden' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Paper sx={{
            boxShadow: 'none',
            background: 'transparent',
            position: 'relative',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: { xs: 'visible', sm: 'hidden' },
            minHeight: 0,
          }}>
            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, flex: 1, overflow: { xs: 'visible', sm: 'auto' }, pb: 2 }}>
              {displayOrders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: '#6b7280' }}>{t('orders.no_orders')}</Typography>
                </Box>
              ) : displayOrders.map((order) => {
                const orderType = getOrderType(order);
                const typeConfig = getOrderTypeConfig(orderType);
                const customerName = (() => {
                  const name = order.customer_name || '';
                  const isDefault = !name || name === 'Laufkunde' || name === 'Walk-in Customer';
                  if (isDefault && orderType === 'dine_in') return (order as any).table_number ? `Tisch ${(order as any).table_number}` : 'Dine-In';
                  return isDefault ? t('orders.walk_in_customer') : name;
                })();
                const orderAnyM = order as any;
                const totalNumM = typeof order.total === 'number' ? order.total : parseFloat(String(order.total)) || 0;
                const itemsSumM = (order.items || []).reduce((s: number, it: any) => {
                  const q = it.quantity || 1;
                  const p = it.unit_price || it.price || (it.total && q ? it.total / q : 0);
                  return s + q * p;
                }, 0);
                const explicitDiscM = parseFloat(orderAnyM.discount_amount) || 0;
                const explicitBeforeM = parseFloat(orderAnyM.total_before_discount) || 0;
                let deliveryFeeM = parseFloat(orderAnyM.delivery_fee) || 0;
                const gapM = totalNumM - (itemsSumM + deliveryFeeM);
                let impliedDiscM = 0;
                if (explicitDiscM === 0 && Math.abs(gapM) > 0.01) {
                  if (gapM < 0) impliedDiscM = -gapM;
                  else if (gapM > 0 && deliveryFeeM === 0 && orderAnyM.order_type === 'delivery') deliveryFeeM = gapM;
                }
                const mobileDiscount = explicitDiscM > 0 ? explicitDiscM : impliedDiscM;
                const mobileBefore = explicitBeforeM > 0 ? explicitBeforeM : (mobileDiscount > 0 ? itemsSumM + deliveryFeeM : totalNumM);
                const mobileReason = orderAnyM.discount_reason
                  ? orderAnyM.discount_reason
                  : orderAnyM.discount_type === 'percentage' && orderAnyM.discount_value
                    ? `${orderAnyM.discount_value}%`
                    : '';
                return (
                  <Box key={order.id} onClick={() => handleViewOrder(order)} sx={{
                    p: 1.5, borderRadius: 2, backgroundColor: '#1e2139', border: '1px solid #2a2d3e',
                    borderLeft: `4px solid ${typeConfig.borderColor}`, cursor: 'pointer',
                    '&:active': { backgroundColor: '#252838' },
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
                          #{order.order_number || order.id}
                        </Typography>
                        <Chip icon={typeConfig.icon} label={typeConfig.label} size="small" sx={{
                          backgroundColor: typeConfig.bgColor, color: typeConfig.color, fontWeight: 600,
                          fontSize: '0.7rem', height: 24, border: `1px solid ${typeConfig.borderColor}`,
                          '& .MuiChip-icon': { color: typeConfig.color },
                        }} />
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '1rem' }}>
                          €{totalNumM.toFixed(2)}
                        </Typography>
                        {mobileDiscount > 0 && (
                          <Typography sx={{ color: '#999', fontSize: '0.65rem', textDecoration: 'line-through', lineHeight: 1 }}>
                            €{mobileBefore.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {mobileDiscount > 0 && (
                      <Box sx={{ mb: 0.75 }}>
                        <Chip
                          label={`Rabatt −€${mobileDiscount.toFixed(2)}${mobileReason ? ' (' + mobileReason + ')' : ''}`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(0, 200, 150,0.15)',
                            color: '#00C896',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            height: 22,
                            border: '1px solid rgba(0, 200, 150,0.4)',
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>{customerName}</Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '0.72rem' }}>
                          {order.items.length} {t('orders.items_count')} · {new Date(order.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                      <Chip icon={getStatusIcon(order.status)} label={getStatusLabel(order.status)}
                        color={getStatusColor(order.status) as any} size="small"
                        sx={{ fontSize: '0.7rem', height: 26 }}
                      />
                    </Box>
                    {/* Quick action buttons */}
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                      {order.status === 'pending' && (
                        <Chip label={t('orders.confirm_order')} size="small" onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}
                          sx={{ backgroundColor: 'rgba(76,175,80,0.15)', color: '#4CAF50', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }} />
                      )}
                      {order.status === 'confirmed' && !(order as any).rider_email && (
                        <Chip label={t('orders.start_preparing')} size="small" onClick={() => handleUpdateOrderStatus(order.id, 'preparing')}
                          sx={{ backgroundColor: 'rgba(255,152,0,0.15)', color: '#FF9800', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }} />
                      )}
                      {order.status === 'preparing' && !(order as any).rider_email && (
                        <Chip label="Ready" size="small" onClick={() => handleUpdateOrderStatus(order.id, 'ready')}
                          sx={{ backgroundColor: 'rgba(33,150,243,0.15)', color: '#2196F3', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer' }} />
                      )}
                      {(order.status === 'ready' || order.status === 'confirmed' || order.status === 'preparing') && (
                        <Chip label="✓" size="small" onClick={() => handleUpdateOrderStatus(order.id, 'completed')}
                          sx={{ backgroundColor: 'rgba(0, 200, 150,0.15)', color: '#00C896', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', minWidth: 32 }} />
                      )}
                      {order.status !== 'completed' && order.status !== 'cancelled'
                        && (order.order_type === 'takeaway' || order.order_type === 'pickup' || order.order_type === 'delivery') && (
                        <Chip label={t('orders.add_items')} size="small"
                          onClick={() => {
                            try {
                              localStorage.setItem('saheeh_edit_order_id', order.id);
                              localStorage.setItem('saheeh_edit_order_number', String(order.order_number || ''));
                              localStorage.setItem('saheeh_edit_order_customer', order.customer_name || '');
                              localStorage.setItem('saheeh_edit_order_type', order.order_type || 'takeaway');
                            } catch {}
                            navigate('/dashboard');
                          }}
                          sx={{ backgroundColor: 'rgba(0, 200, 150,0.15)', color: '#00C896', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }} />
                      )}
                      {order.status !== 'completed' && order.status !== 'cancelled' && (
                        <Chip
                          label={mobileDiscount > 0 ? `🏷 ${t('orders.change_discount')}` : `🏷 ${t('orders.apply_discount')}`}
                          size="small"
                          onClick={() => openDiscountFor(order)}
                          sx={{ backgroundColor: 'rgba(0, 200, 150,0.15)', color: '#00C896', fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer' }}
                        />
                      )}
                      {order.status !== 'completed' && order.status !== 'cancelled' && (
                        <Chip label="✕" size="small" onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                          sx={{ backgroundColor: 'rgba(244,67,54,0.12)', color: '#F44336', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', minWidth: 32 }} />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Desktop Table View */}
            <TableContainer sx={{
              flex: 1,
              overflow: 'auto',
              overflowX: 'auto',
              display: { xs: 'none', md: 'block' },
              borderRadius: '14px',
              border: '1px solid rgba(255,255,255,0.05)',
              background: 'linear-gradient(180deg, #1a1d29 0%, #1e2139 100%)',
              '&::-webkit-scrollbar': {
                width: '10px', height: '10px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255,255,255,0.08)',
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 200, 150,0.3)',
                },
              },
            }}>
              <Table stickyHeader sx={{
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  padding: '10px 12px',
                },
                '& .MuiTableCell-head': {
                  backgroundColor: '#0f1117 !important',
                  color: '#8a93b0 !important',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  py: '12px',
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('orders.order_id')}</TableCell>
                    <TableCell>{t('orders.type')}</TableCell>
                    <TableCell>{t('orders.customer')}</TableCell>
                    <TableCell align="center">{t('orders.items')}</TableCell>
                    <TableCell align="right">{t('orders.total')}</TableCell>
                    <TableCell>{t('orders.discount')}</TableCell>
                    <TableCell>{t('orders.status')}</TableCell>
                    <TableCell>{t('orders.date')} / {t('orders.time')}</TableCell>
                    <TableCell align="center" sx={{ minWidth: 160 }}>{t('orders.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayOrders.map((order, idx) => {
                    const orderType = getOrderType(order);
                    const typeConfig = getOrderTypeConfig(orderType);
                    const typeAccent = orderType === 'delivery' ? '#f97316' : orderType === 'pickup' ? '#22c55e' : '#3b82f6';
                    const typeGradient = orderType === 'delivery'
                      ? 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)'
                      : orderType === 'pickup'
                        ? 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)'
                        : 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)';
                    return (
                    <TableRow key={order.id} sx={{
                      backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 200, 150,0.04)',
                        '& .row-accent': { opacity: 1, width: '5px' },
                      },
                      position: 'relative',
                    }}>
                      {/* Order ID */}
                      <TableCell sx={{ position: 'relative', pl: '18px !important' }}>
                        <Box className="row-accent" sx={{
                          position: 'absolute', left: 0, top: 6, bottom: 6, width: '3px',
                          background: typeGradient, opacity: 0.6, transition: 'all 0.18s ease',
                          borderRadius: '0 3px 3px 0',
                        }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                          <Typography sx={{
                            fontWeight: 800, color: '#fff', fontSize: '0.95rem',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            letterSpacing: '-0.3px',
                          }}>
                            #{order.order_number || order.id}
                          </Typography>
                          {order.is_split_payment && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                px: 0.75, py: 0.2, borderRadius: '5px',
                                backgroundColor: 'rgba(124,58,237,0.15)',
                                border: '1px solid rgba(124,58,237,0.4)',
                                color: '#c4b5fd', fontWeight: 700, fontSize: '0.65rem',
                              }}>
                                ✂ {(order as any).split_person_name || 'Split'}
                              </Box>
                              {(order as any).original_order_number && (
                                <Typography sx={{ color: '#7c3aed', fontWeight: 700, fontSize: '0.62rem' }}>
                                  ← #{(order as any).original_order_number}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                            px: 1.1, py: 0.5, borderRadius: '8px',
                            backgroundColor: `${typeAccent}15`,
                            border: `1px solid ${typeAccent}35`,
                            color: typeAccent, fontWeight: 700, fontSize: '0.76rem',
                            '& svg': { fontSize: 14 },
                          }}>
                            {typeConfig.icon}
                            {typeConfig.label}
                          </Box>
                          {(order as any).source === 'qr_order' && (
                            <Box sx={{
                              px: 0.75, py: 0.3, borderRadius: '5px',
                              backgroundColor: 'rgba(156,39,176,0.15)',
                              border: '1px solid rgba(156,39,176,0.4)',
                              color: '#ce93d8', fontWeight: 800, fontSize: '0.62rem',
                              letterSpacing: '0.3px',
                            }}>
                              QR
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Customer */}
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Box>
                          <Typography sx={{
                            fontWeight: 600, color: '#e8eaf6', fontSize: '0.88rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {(() => {
                              const name = order.customer_name || '';
                              const isDefault = !name || name === 'Laufkunde' || name === 'Walk-in Customer';
                              if (isDefault && orderType === 'dine_in') {
                                return (order as any).table_number ? `Tisch ${(order as any).table_number}` : 'Dine-In';
                              }
                              return isDefault ? t('orders.walk_in_customer') : name;
                            })()}
                          </Typography>
                          {orderType === 'delivery' && (order as any).customer_address && (
                            <Typography sx={{
                              display: 'block', color: '#6b7280', fontSize: '0.72rem', fontWeight: 500,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.25,
                            }}>
                              📍 {(order as any).customer_address}
                            </Typography>
                          )}
                          {orderType === 'dine_in' && (order as any).table_number && (
                            <Typography sx={{ display: 'block', color: '#60a5fa', fontWeight: 700, fontSize: '0.7rem', mt: 0.25 }}>
                              🪑 {t('orders.table')} {(order as any).table_number}
                            </Typography>
                          )}
                          {order.is_split_payment && order.split_person_index && order.split_total_people && (
                            <Typography sx={{ display: 'block', color: '#a78bfa', fontWeight: 600, fontSize: '0.7rem', mt: 0.25 }}>
                              {order.split_person_index}/{order.split_total_people}{(order as any).partial_split ? ' · Partial' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Items */}
                      <TableCell align="center">
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 42, height: 30, px: 1, borderRadius: '8px',
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          color: '#e8eaf6', fontWeight: 700, fontSize: '0.85rem',
                        }}>
                          {order.items.length}
                        </Box>
                      </TableCell>

                      {(() => {
                        const orderAny = order as any;
                        const totalNum = typeof order.total === 'number' ? order.total : parseFloat(String(order.total)) || 0;
                        const itemsSum = (order.items || []).reduce((s: number, it: any) => {
                          const q = it.quantity || 1;
                          const p = it.unit_price || it.price || (it.total && q ? it.total / q : 0);
                          return s + q * p;
                        }, 0);
                        const explicitDiscount = parseFloat(orderAny.discount_amount) || 0;
                        const explicitBefore = parseFloat(orderAny.total_before_discount) || 0;
                        let deliveryFee = parseFloat(orderAny.delivery_fee) || 0;
                        const gap = totalNum - (itemsSum + deliveryFee);
                        let impliedDiscount = 0;
                        if (explicitDiscount === 0 && Math.abs(gap) > 0.01) {
                          if (gap < 0) impliedDiscount = -gap;
                          else if (gap > 0 && deliveryFee === 0 && orderAny.order_type === 'delivery') deliveryFee = gap;
                        }
                        const discount = explicitDiscount > 0 ? explicitDiscount : impliedDiscount;
                        const before = explicitBefore > 0 ? explicitBefore : (discount > 0 ? itemsSum + deliveryFee : totalNum);
                        const reasonStr = orderAny.discount_reason
                          ? orderAny.discount_reason
                          : orderAny.discount_type === 'percentage' && orderAny.discount_value
                            ? `${orderAny.discount_value}%`
                            : '';
                        return (
                          <>
                            <TableCell align="right">
                              <Box>
                                <Typography sx={{
                                  fontWeight: 800, color: '#00C896', fontSize: '1rem',
                                  letterSpacing: '-0.3px',
                                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                }}>
                                  €{totalNum.toFixed(2)}
                                </Typography>
                                {discount > 0 && (
                                  <Typography sx={{
                                    color: '#6b7280', fontSize: '0.68rem',
                                    textDecoration: 'line-through', fontWeight: 500,
                                  }}>
                                    €{before.toFixed(2)}
                                  </Typography>
                                )}
                                {!discount && orderAny.original_total && orderAny.original_total !== totalNum && (
                                  <Typography sx={{
                                    color: '#6b7280', fontSize: '0.68rem',
                                    textDecoration: 'line-through', fontWeight: 500,
                                  }}>
                                    €{orderAny.original_total.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {discount > 0 ? (
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                  px: 1, py: 0.4, borderRadius: '7px',
                                  backgroundColor: 'rgba(0, 200, 150,0.12)',
                                  border: '1px solid rgba(0, 200, 150,0.35)',
                                  color: '#00C896', fontWeight: 700, fontSize: '0.72rem',
                                }}>
                                  <LocalOffer sx={{ fontSize: 12 }} />
                                  −€{discount.toFixed(2)}{reasonStr ? ` · ${reasonStr}` : ''}
                                </Box>
                              ) : (
                                <Typography sx={{ color: '#4b5563', fontSize: '0.85rem' }}>—</Typography>
                              )}
                            </TableCell>
                          </>
                        );
                      })()}

                      {/* Status */}
                      <TableCell>
                        {(() => {
                          const st = (order.status || 'pending').toLowerCase();
                          const config: Record<string, { bg: string; color: string; border: string; dot: string }> = {
                            pending: { bg: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: 'rgba(245,158,11,0.35)', dot: '#f59e0b' },
                            confirmed: { bg: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: 'rgba(59,130,246,0.35)', dot: '#3b82f6' },
                            preparing: { bg: 'rgba(251,146,60,0.12)', color: '#fb923c', border: 'rgba(251,146,60,0.35)', dot: '#f97316' },
                            ready: { bg: 'rgba(34,197,94,0.12)', color: '#4ade80', border: 'rgba(34,197,94,0.35)', dot: '#22c55e' },
                            delivered: { bg: 'rgba(0, 200, 150,0.12)', color: '#00C896', border: 'rgba(0, 200, 150,0.35)', dot: '#00C896' },
                            completed: { bg: 'rgba(0, 200, 150,0.12)', color: '#00C896', border: 'rgba(0, 200, 150,0.35)', dot: '#00C896' },
                            cancelled: { bg: 'rgba(239,68,68,0.12)', color: '#f87171', border: 'rgba(239,68,68,0.35)', dot: '#ef4444' },
                          };
                          const c = config[st] || { bg: 'rgba(107,114,128,0.12)', color: '#9ca3af', border: 'rgba(107,114,128,0.35)', dot: '#6b7280' };
                          return (
                            <Box sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.6,
                              px: 1.1, py: 0.5, borderRadius: '999px',
                              backgroundColor: c.bg, border: `1px solid ${c.border}`,
                              color: c.color, fontWeight: 700, fontSize: '0.72rem',
                            }}>
                              <Box sx={{
                                width: 6, height: 6, borderRadius: '50%',
                                backgroundColor: c.dot,
                                boxShadow: `0 0 0 3px ${c.dot}25`,
                                animation: ['pending', 'preparing'].includes(st) ? 'pulse 2s ease-in-out infinite' : 'none',
                                '@keyframes pulse': {
                                  '0%, 100%': { opacity: 1 },
                                  '50%': { opacity: 0.4 },
                                },
                              }} />
                              {getStatusLabel(order.status)}
                            </Box>
                          );
                        })()}
                      </TableCell>

                      {/* Date / Time */}
                      <TableCell>
                        <Typography sx={{
                          color: '#e8eaf6', fontWeight: 600, fontSize: '0.8rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        }}>
                          {new Date(order.created_at).toLocaleDateString('en-US', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })}
                        </Typography>
                        <Typography sx={{
                          color: '#8a93b0', fontWeight: 500, fontSize: '0.76rem',
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        }}>
                          {new Date(order.created_at).toLocaleTimeString('en-US', {
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ minWidth: 160 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {/* View Order Button */}
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                            sx={actionBtnSx('#1976D2', 'rgba(33,150,243,0.12)')}
                            title={t('orders.view_order_details')}
                          >
                            <Visibility />
                          </IconButton>

                          {/* Status Update Buttons */}
                          {order.status === 'pending' && (
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateOrderStatus(order.id, 'confirmed')}
                              sx={actionBtnSx('#4CAF50', 'rgba(76,175,80,0.12)')}
                              title={t('orders.confirm_order')}
                            >
                              <CheckCircle />
                            </IconButton>
                          )}

                          {/* Start Preparing */}
                          {order.status === 'confirmed' && !(order as any).rider_email && (
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateOrderStatus(order.id, 'preparing')}
                              sx={actionBtnSx('#FF9800', 'rgba(255,152,0,0.12)')}
                              title={t('orders.start_preparing')}
                            >
                              <Restaurant />
                            </IconButton>
                          )}

                          {/* Mark Ready */}
                          {order.status === 'preparing' && !(order as any).rider_email && (
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateOrderStatus(order.id, 'ready')}
                              sx={actionBtnSx('#2196F3', 'rgba(33,150,243,0.12)')}
                              title={t('orders.mark_ready')}
                            >
                              <CheckCircle />
                            </IconButton>
                          )}

                          {/* Assign Driver */}
                          {!(order as any).rider_email &&
                            ['confirmed', 'preparing', 'ready'].includes(order.status) && (
                            <IconButton
                              size="small"
                              onClick={() => handleAssignDelivery(order)}
                              sx={actionBtnSx('#1976D2', 'rgba(33,150,243,0.12)')}
                              title={t('orders.assign_delivery_driver')}
                            >
                              <LocalShipping />
                            </IconButton>
                          )}

                          {/* Driver already assigned (disabled, informational) */}
                          {(order as any).rider_email && (
                            <IconButton
                              size="small"
                              disabled
                              sx={{
                                ...actionBtnSx('#4caf50', 'rgba(76,175,80,0.18)'),
                                cursor: 'default',
                                '&.Mui-disabled': { color: '#4caf50', backgroundColor: 'rgba(76,175,80,0.18)' },
                              }}
                              title={`${t('orders.driver_assigned')}: ${(order as any).assigned_driver_name || (order as any).rider_email}`}
                            >
                              <LocalShipping />
                            </IconButton>
                          )}

                          {/* Print Receipt — one-click direct print to Epson cashier printer */}
                          <IconButton
                            size="small"
                            onClick={() => handleDirectPrintReceipt(order)}
                            sx={actionBtnSx('#1976D2', 'rgba(33,150,243,0.12)')}
                            title={t('orders.print_receipt')}
                          >
                            <Receipt />
                          </IconButton>

                          {/* Apply Discount — POS-only, works on any order type */}
                          {order.status !== 'completed' && order.status !== 'cancelled' && (
                            <IconButton
                              size="small"
                              onClick={() => openDiscountFor(order)}
                              sx={actionBtnSx('#00C896', 'rgba(0, 200, 150,0.15)')}
                              title={(order as any).discount_amount > 0 ? t('orders.change_discount') : t('orders.apply_discount')}
                            >
                              <LocalOffer />
                            </IconButton>
                          )}

                          {/* Complete Order — dine-in, takeaway/pickup, or delivery without a driver
                              (delivery orders with a driver are completed by the rider app) */}
                          {((order as any).order_type === 'dine_in'
                              || (order as any).order_type === 'restaurant'
                              || (order as any).order_type === 'takeaway'
                              || (order as any).order_type === 'pickup'
                              || ((order as any).order_type === 'delivery' && !(order as any).rider_email)) &&
                            (order.status === 'ready' || order.status === 'preparing' || order.status === 'confirmed') && (
                            <IconButton
                              size="small"
                              onClick={() => handleCompleteOrder(order.id)}
                              sx={actionBtnSx('#4CAF50', 'rgba(76,175,80,0.12)')}
                              title={t('orders.complete_order')}
                            >
                              <Done />
                            </IconButton>
                          )}

                          {/* Add Items — consistent icon button to match the row */}
                          {order.status !== 'completed' && order.status !== 'cancelled'
                            && (order.order_type === 'takeaway' || order.order_type === 'pickup' || order.order_type === 'delivery') && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                try {
                                  localStorage.setItem('saheeh_edit_order_id', order.id);
                                  localStorage.setItem('saheeh_edit_order_number', String(order.order_number || ''));
                                  localStorage.setItem('saheeh_edit_order_customer', order.customer_name || '');
                                  localStorage.setItem('saheeh_edit_order_type', order.order_type || 'takeaway');
                                } catch {}
                                navigate('/dashboard');
                              }}
                              sx={actionBtnSx('#00C896', 'rgba(0, 200, 150,0.15)')}
                              title={t('orders.add_items_tooltip')}
                            >
                              <AddShoppingCart />
                            </IconButton>
                          )}

                          {/* Cancel Order */}
                          {(order.status === 'pending' || order.status === 'confirmed') && (
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')}
                              sx={actionBtnSx('#f44336', 'rgba(244,67,54,0.12)')}
                              title={t('orders.cancel_order')}
                            >
                              <Cancel />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {displayOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: 'center', py: 10, border: 'none' }}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 72, height: 72, borderRadius: '50%', mb: 2,
                          background: 'linear-gradient(135deg, rgba(0, 200, 150,0.08) 0%, rgba(59,130,246,0.08) 100%)',
                          border: '1px solid rgba(0, 200, 150,0.2)',
                        }}>
                          <Receipt sx={{ fontSize: 32, color: '#00C896' }} />
                        </Box>
                        <Typography sx={{ mb: 0.5, fontWeight: 700, color: '#e8eaf6', fontSize: '1.05rem' }}>
                          {t('orders.no_orders_found')}
                        </Typography>
                        <Typography sx={{ color: '#6b7280', fontSize: '0.85rem' }}>
                          {orderTypeFilter !== 'all'
                            ? t('orders.try_different_filter')
                            : t('orders.new_orders_appear')
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          </Box>

          {/* View Order Dialog - Custom Modal (Same Style as Customer Information Modal) */}
          {openViewDialog && (
            <Box
              data-modal-overlay="true"
              onClick={(e) => {
                // Only close if clicking the overlay, not the dialog content
                if (e.target === e.currentTarget) {
                  setOpenViewDialog(false);
                }
              }}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'auto'
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#252838',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '1px solid #3a3d52',
                  maxWidth: '700px',
                  width: '90%',
                  maxHeight: '85vh',
                  overflow: 'auto',
                  pointerEvents: 'auto',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with order number, type badge, and status */}
                <Box sx={{ p: 2, pb: 1.5, borderBottom: '1px solid #3a3d52', flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem', letterSpacing: '-0.3px' }}>
                        #{selectedOrder?.order_number || selectedOrder?.id}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888', fontSize: '0.75rem' }}>
                        {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      {/* Order type chip */}
                      <Chip
                        size="small"
                        label={selectedOrder?.order_type === 'delivery' ? t('orders.type_delivery') : selectedOrder?.order_type === 'takeaway' ? t('orders.type_pickup') : t('orders.type_dine_in')}
                        sx={{
                          fontWeight: 600, fontSize: '0.7rem', height: 24,
                          backgroundColor: selectedOrder?.order_type === 'delivery' ? '#E3F2FD' : selectedOrder?.order_type === 'takeaway' ? '#FFF3E0' : '#E8F5E9',
                          color: selectedOrder?.order_type === 'delivery' ? '#1565C0' : selectedOrder?.order_type === 'takeaway' ? '#E65100' : '#2E7D32',
                        }}
                      />
                      {/* Status chip */}
                      {(() => {
                        const st = (selectedOrder?.status || 'pending') as string;
                        const bgMap: Record<string, string> = { completed: '#E8F5E9', delivered: '#E8F5E9', cancelled: '#FFEBEE', preparing: '#FFF8E1', ready: '#E0F7FA' };
                        const colorMap: Record<string, string> = { completed: '#2E7D32', delivered: '#2E7D32', cancelled: '#C62828', preparing: '#F57F17', ready: '#00695C' };
                        return (
                          <Chip
                            size="small"
                            label={st.charAt(0).toUpperCase() + st.slice(1)}
                            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24, backgroundColor: bgMap[st] || '#F3E5F5', color: colorMap[st] || '#6A1B9A' }}
                          />
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Summary bar: Total | Items | Payment | Table */}
                  {selectedOrder && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pb: 0.5, flexWrap: 'wrap' }}>
                      <Box sx={{ textAlign: 'center', minWidth: 70 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#2E7D32' }}>
                          {'\u20AC'}{typeof selectedOrder.total === 'number' ? selectedOrder.total.toFixed(2) : selectedOrder.total || '0.00'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total</Typography>
                      </Box>
                      <Box sx={{ borderLeft: '1px solid #3a3d52', pl: 2, textAlign: 'center', minWidth: 50 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                          {selectedOrder.items?.length || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('orders.items')}</Typography>
                      </Box>
                      <Box sx={{ borderLeft: '1px solid #3a3d52', pl: 2, textAlign: 'center', minWidth: 60 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                          {(selectedOrder as any).payment_method || (selectedOrder as any).paymentMethod || 'Cash'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment</Typography>
                      </Box>
                      {selectedOrder.order_type === 'dine_in' && (selectedOrder as any).table_number && (
                        <Box sx={{ borderLeft: '1px solid #3a3d52', pl: 2, textAlign: 'center', minWidth: 50 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            T{(selectedOrder as any).table_number}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Table</Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Split Payment Info Banner */}
                {selectedOrder?.is_split_payment && (
                  <Box sx={{ mx: 2, mt: 1, p: 1.5, borderRadius: 2, backgroundColor: 'rgba(156, 39, 176, 0.08)', border: '1px solid rgba(156, 39, 176, 0.3)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontSize: '1rem' }}>✂️</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '0.85rem' }}>
                        SPLIT TABLE {(selectedOrder as any).table_number || ''}
                      </Typography>
                      {(selectedOrder as any).original_order_number && (
                        <Chip label={`Original #${(selectedOrder as any).original_order_number}`} size="small" sx={{ backgroundColor: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '0.75rem', height: 22 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {selectedOrder.split_person_name && (
                        <Typography variant="body2" sx={{ color: '#b794f4', fontWeight: 600, fontSize: '0.8rem' }}>
                          👤 {selectedOrder.split_person_name}
                          {selectedOrder.split_person_index && selectedOrder.split_total_people ? ` (${selectedOrder.split_person_index}/${selectedOrder.split_total_people})` : ''}
                        </Typography>
                      )}
                      {(selectedOrder as any).partial_split && (
                        <Chip label="Partial Pay" size="small" sx={{ backgroundColor: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                      )}
                      <Typography variant="body2" sx={{ color: '#b794f4', fontSize: '0.8rem' }}>
                        💰 €{selectedOrder.total?.toFixed(2)} ({(selectedOrder as any).payment_method || selectedOrder.paymentMethod || 'cash'})
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Box sx={{ p: 2, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {selectedOrder && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                      {/* Customer Section - Only show fields that have data */}
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9ca3af' }}>
                            {t('orders.customer_information')}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingCustomerName(selectedOrder.customer_name || '');
                              setEditingCustomerAddress((selectedOrder as any).customer_address || '');
                              setOpenEditCustomerModal(true);
                            }}
                            sx={{ color: '#1976D2', p: 0.5 }}
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Box sx={{ border: '1px solid #3a3d52', borderRadius: 1.5, overflow: 'hidden' }}>
                          {/* Customer name row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', p: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                            <Typography variant="caption" sx={{ width: 80, color: '#888', fontWeight: 500, flexShrink: 0 }}>Name</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                              {(selectedOrder as any).customer_first_name && (selectedOrder as any).customer_last_name
                                ? `${(selectedOrder as any).customer_first_name} ${(selectedOrder as any).customer_last_name}`
                                : selectedOrder.customer_name || t('orders.walk_in_customer')}
                            </Typography>
                          </Box>
                          {/* Phone - only if provided */}
                          {(selectedOrder as any).customer_phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', p: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                              <Typography variant="caption" sx={{ width: 80, color: '#888', fontWeight: 500, flexShrink: 0 }}>Phone</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{(selectedOrder as any).customer_phone}</Typography>
                            </Box>
                          )}
                          {/* Address - only if provided */}
                          {((selectedOrder as any).customer_address || selectedOrder.delivery_address) && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', p: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
                              <Typography variant="caption" sx={{ width: 80, color: '#888', fontWeight: 500, flexShrink: 0, pt: 0.25 }}>Address</Typography>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {(selectedOrder as any).customer_address || selectedOrder.delivery_address}
                                </Typography>
                                {((selectedOrder as any).customer_postcode || (selectedOrder as any).customer_city) && (
                                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                                    {[(selectedOrder as any).customer_postcode, (selectedOrder as any).customer_city].filter(Boolean).join(' ')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          {/* Created by row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', p: '8px 12px', backgroundColor: '#1e2139' }}>
                            <Typography variant="caption" sx={{ width: 80, color: '#888', fontWeight: 500, flexShrink: 0 }}>Staff</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#1976D2', fontSize: '0.8rem' }}>
                              {(selectedOrder as any).created_by_name || (selectedOrder as any).created_by_email || (selectedOrder as any).created_by || 'System'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Live Driver Tracking Map */}
                      {(selectedOrder as any).rider_email && (
                        <Box>
                          <LiveDriverMap
                            driverEmail={(selectedOrder as any).rider_email}
                            driverName={(selectedOrder as any).assigned_driver_name || 'Driver'}
                            deliveryAddress={(selectedOrder as any).delivery_address || (selectedOrder as any).customer_address}
                            deliveryLatitude={(selectedOrder as any).delivery_latitude}
                            deliveryLongitude={(selectedOrder as any).delivery_longitude}
                            latitude={driverLocation?.latitude}
                            longitude={driverLocation?.longitude}
                          />
                        </Box>
                      )}

                      {/* Delivery Location (if no rider assigned yet) */}
                      {!(selectedOrder as any).rider_email &&
                        ((selectedOrder as any).delivery_address || (selectedOrder as any).delivery_latitude) && (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9ca3af' }}>
                                {t('orders.delivery_location')}
                              </Typography>
                              <Chip
                                icon={(selectedOrder as any).location_confirmed ? <CheckCircle sx={{ fontSize: 14 }} /> : <LocationOff sx={{ fontSize: 14 }} />}
                                label={(selectedOrder as any).location_confirmed ? t('orders.location_confirmed') : t('orders.not_confirmed')}
                                size="small"
                                sx={{
                                  height: 22, fontSize: '0.65rem', fontWeight: 600,
                                  backgroundColor: (selectedOrder as any).location_confirmed ? '#E8F5E9' : '#FFF3E0',
                                  color: (selectedOrder as any).location_confirmed ? '#2E7D32' : '#E65100',
                                }}
                              />
                            </Box>
                            <Box sx={{ border: '1px solid #3a3d52', borderRadius: 1.5, p: 1.5 }}>
                              {(selectedOrder as any).delivery_address && (
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                  {(selectedOrder as any).delivery_address}
                                </Typography>
                              )}
                              {(selectedOrder as any).delivery_latitude && (selectedOrder as any).delivery_longitude && (
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                  GPS: {(selectedOrder as any).delivery_latitude.toFixed(6)}, {(selectedOrder as any).delivery_longitude.toFixed(6)}
                                </Typography>
                              )}
                              {(selectedOrder as any).location_confirmed && (selectedOrder as any).location_confirmed_by && (
                                <Typography variant="caption" sx={{ color: '#4CAF50', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                  Confirmed by: {(selectedOrder as any).location_confirmed_by_name || (selectedOrder as any).location_confirmed_by}
                                  {(selectedOrder as any).location_confirmed_at && (
                                    <span> {'\u2022'} {new Date((selectedOrder as any).location_confirmed_at.toDate?.() || (selectedOrder as any).location_confirmed_at).toLocaleString()}</span>
                                  )}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                      {/* Order Items */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#9ca3af', mb: 0.75 }}>
                          {t('order_details.order_items')} ({selectedOrder.items?.length || 0})
                        </Typography>

                        {/* Last edit reason — surfaces void/edit reason for manager visibility */}
                        {(selectedOrder as any).last_modification_reason && (
                          <Box sx={{
                            mb: 1, px: 1.4, py: 0.9,
                            backgroundColor: 'rgba(255, 152, 0, 0.10)',
                            border: '1px solid rgba(255, 152, 0, 0.45)',
                            borderLeft: '3px solid #FF9800',
                            borderRadius: 1,
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                          }}>
                            <Typography component="span" sx={{
                              color: '#FFB300', fontSize: '0.62rem', fontWeight: 800,
                              letterSpacing: '1px', textTransform: 'uppercase',
                              flexShrink: 0, lineHeight: 1.6,
                            }}>
                              Last edit
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{
                                color: '#FFECB3', fontSize: '0.78rem', fontWeight: 600,
                                lineHeight: 1.4, wordBreak: 'break-word',
                              }}>
                                {(selectedOrder as any).last_modification_reason}
                              </Typography>
                              {(selectedOrder as any).last_modified_by && (
                                <Typography sx={{ color: '#bcaa83', fontSize: '0.68rem', mt: 0.3 }}>
                                  by {(selectedOrder as any).last_modified_by}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                        {!selectedOrder.items || selectedOrder.items.length === 0 ? (
                          <Box sx={{ p: 3, border: '1px solid #3a3d52', borderRadius: 1.5, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">{t('order_details.no_items')}</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ border: '1px solid #3a3d52', borderRadius: 1.5, overflow: 'hidden' }}>
                            {selectedOrder.items.map((item, index) => {
                              const qty = typeof item.quantity === 'number' ? item.quantity : parseFloat(String(item.quantity)) || 0;
                              const price = typeof item.unit_price === 'number' ? item.unit_price : parseFloat(String(item.unit_price)) || 0;
                              const lineTotal = (qty * price).toFixed(2);
                              return (
                                <Box key={index} sx={{
                                  p: '10px 12px',
                                  borderBottom: index < (selectedOrder.items?.length || 0) - 1 ? '1px solid #f0f0f0' : 'none',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  '&:hover': { backgroundColor: '#1e2139' }
                                }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d0d0', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                        {qty}x
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#d0d0d0', fontSize: '0.85rem' }}>
                                        {item.name || item.menu_item_name || 'Unknown Item'}
                                      </Typography>
                                    </Box>
                                    {qty > 1 && (
                                      <Typography variant="caption" sx={{ color: '#999', fontSize: '0.7rem', ml: 2.5 }}>
                                        @ {'\u20AC'}{price.toFixed(2)} each
                                      </Typography>
                                    )}
                                    {((item as any).addons && Array.isArray((item as any).addons) && (item as any).addons.length > 0) && (
                                      <Box sx={{ ml: 2.5, mt: 0.25 }}>
                                        {(item as any).addons.map((addon: any, ai: number) => (
                                          <Typography key={ai} variant="caption" sx={{ color: '#1976D2', fontSize: '0.7rem', display: 'block' }}>
                                            + {addon.quantity ? `${addon.quantity}x ` : ''}{addon.name_de || addon.name}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}
                                    {((item as any).notes && (item as any).notes.trim() !== '') && (
                                      <Box sx={{
                                        ml: 2.5, mt: 0.75,
                                        p: '6px 10px',
                                        backgroundColor: 'rgba(255, 179, 0, 0.15)',
                                        border: '1px solid rgba(255, 179, 0, 0.55)',
                                        borderLeft: '4px solid #FFB300',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 0.75,
                                      }}>
                                        <Typography component="span" sx={{
                                          color: '#FFB300',
                                          fontSize: '0.62rem',
                                          fontWeight: 800,
                                          letterSpacing: '1px',
                                          textTransform: 'uppercase',
                                          flexShrink: 0,
                                          lineHeight: 1.6,
                                        }}>
                                          📝 Note
                                        </Typography>
                                        <Typography sx={{
                                          color: '#FFECB3',
                                          fontSize: '0.82rem',
                                          fontWeight: 600,
                                          fontStyle: 'italic',
                                          wordBreak: 'break-word',
                                          whiteSpace: 'pre-wrap',
                                          lineHeight: 1.4,
                                        }}>
                                          {(item as any).notes}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                      {'\u20AC'}{lineTotal}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditItem(item, index)}
                                      sx={{ color: '#bbb', p: 0.25, '&:hover': { color: '#1976D2' } }}
                                    >
                                      <Edit sx={{ fontSize: 14 }} />
                                    </IconButton>
                                    {/* Remove (void) button — works for delivery / pickup / QR / dine-in */}
                                    {(() => {
                                      const orderStatusLower = (selectedOrder?.status || '').toLowerCase();
                                      const isVoidableStatus = !['completed', 'cancelled', 'delivered', 'refunded'].includes(orderStatusLower);
                                      if (!isVoidableStatus || !selectedOrder?.id) return null;
                                      return (
                                        <IconButton
                                          size="small"
                                          title="Remove item"
                                          onClick={() => {
                                            setVoidItemTarget({
                                              orderId: selectedOrder.id,
                                              orderNumber: selectedOrder.order_number || '',
                                              itemIndex: index,
                                              itemLabel: `${qty}× ${item.name || item.menu_item_name || 'Item'}`,
                                              itemPrice: parseFloat(String(lineTotal)) || 0,
                                            });
                                            setVoidItemReason('');
                                          }}
                                          sx={{ color: '#bbb', p: 0.25, '&:hover': { color: '#d32f2f' } }}
                                        >
                                          <Close sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      );
                                    })()}
                                  </Box>
                                </Box>
                              );
                            })}
                            {/* Breakdown: Items sum / Delivery / Rabatt / Total */}
                            {(() => {
                              const orderAny = selectedOrder as any;
                              const totalNum = typeof selectedOrder.total === 'number'
                                ? selectedOrder.total
                                : parseFloat(String(selectedOrder.total)) || 0;
                              const itemsSum = (selectedOrder.items || []).reduce((s: number, it: any) => {
                                const q = it.quantity || 1;
                                const p = it.unit_price || it.price || (it.total && q ? it.total / q : 0);
                                return s + q * p;
                              }, 0);
                              const explicitDiscount = orderAny.discount_amount || 0;
                              const explicitBefore = orderAny.total_before_discount || 0;
                              const orderTypeLocal = orderAny.order_type || 'dine_in';
                              let deliveryFee = orderAny.delivery_fee || 0;
                              const gap = totalNum - (itemsSum + deliveryFee);
                              let impliedDiscount = 0;
                              if (explicitDiscount === 0 && Math.abs(gap) > 0.01) {
                                if (gap < 0) {
                                  impliedDiscount = -gap;
                                } else if (gap > 0 && deliveryFee === 0 && orderTypeLocal === 'delivery') {
                                  deliveryFee = gap;
                                }
                              }
                              const discount = explicitDiscount > 0 ? explicitDiscount : impliedDiscount;
                              const beforeDiscount = explicitBefore > 0 ? explicitBefore : (discount > 0 ? itemsSum + deliveryFee : totalNum);
                              const discountReasonStr = orderAny.discount_reason
                                ? orderAny.discount_reason
                                : orderAny.discount_type === 'percentage' && orderAny.discount_value
                                  ? `${orderAny.discount_value}%`
                                  : '';

                              return (
                                <>
                                  {(deliveryFee > 0 || discount > 0) && (
                                    <Box sx={{ px: '12px', py: '8px', borderTop: '1px solid #3a3d52', backgroundColor: '#1a1d2e' }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#cfd2d9', mb: 0.5 }}>
                                        <span>Subtotal</span>
                                        <span>€{itemsSum.toFixed(2)}</span>
                                      </Box>
                                      {deliveryFee > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#cfd2d9', mb: 0.5 }}>
                                          <span>Delivery Fee</span>
                                          <span>€{deliveryFee.toFixed(2)}</span>
                                        </Box>
                                      )}
                                      {discount > 0 && (
                                        <>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#cfd2d9', mb: 0.5, pt: 0.5, borderTop: '1px dashed #3a3d52' }}>
                                            <span style={{ fontWeight: 600 }}>Zwischensumme</span>
                                            <span style={{ fontWeight: 600 }}>€{beforeDiscount.toFixed(2)}</span>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#00C896', fontWeight: 600, mb: 0.5 }}>
                                            <span>Rabatt{discountReasonStr ? ` (${discountReasonStr})` : ''}</span>
                                            <span>−€{discount.toFixed(2)}</span>
                                          </Box>
                                        </>
                                      )}
                                    </Box>
                                  )}
                                  <Box sx={{ p: '10px 12px', borderTop: '2px solid #3a3d52', backgroundColor: '#1e2139', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Total</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#2E7D32' }}>
                                      €{totalNum.toFixed(2)}
                                    </Typography>
                                  </Box>
                                </>
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
                {/* Footer Actions */}
                <Box sx={{ p: 1.5, borderTop: '1px solid #3a3d52', flexShrink: 0 }}>
                  {/* Status actions row */}
                  {selectedOrder && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {/* Status change buttons - only if no driver assigned */}
                      {!(selectedOrder as any).rider_email && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleUpdateStatus(selectedOrder.id, 'preparing')}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderColor: '#1976D2', color: '#1976D2', flex: 1, minWidth: 0 }}
                          >
                            {t('orders.start_preparing')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleUpdateStatus(selectedOrder.id, 'ready')}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderColor: '#4CAF50', color: '#4CAF50', flex: 1, minWidth: 0 }}
                          >
                            {t('orders.mark_ready')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleUpdateStatus(selectedOrder.id, 'completed')}
                            sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderColor: '#2E7D32', color: '#2E7D32', flex: 1, minWidth: 0 }}
                          >
                            Complete
                          </Button>
                        </>
                      )}

                      {/* Assign driver button */}
                      {!(selectedOrder as any).rider_email && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LocalShipping sx={{ fontSize: 16 }} />}
                          onClick={() => { handleAssignDelivery(selectedOrder); setOpenViewDialog(false); }}
                          sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, borderColor: '#1976D2', color: '#1976D2', flex: 1, minWidth: 0 }}
                        >
                          {t('order_details.assign_driver')}
                        </Button>
                      )}

                      {/* Driver already assigned */}
                      {(selectedOrder as any).rider_email && (
                        <Chip
                          icon={<LocalShipping sx={{ fontSize: 16 }} />}
                          label={`${t('order_details.driver')}: ${(selectedOrder as any).assigned_driver_name || (selectedOrder as any).rider_email}`}
                          size="small"
                          sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      )}

                      {/* Fix Payment */}
                      {((selectedOrder.status === 'delivered' || (selectedOrder.status as string) === 'completed') &&
                        ((selectedOrder as any).payment_status !== 'completed' && (selectedOrder as any).paymentStatus !== 'completed')) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<Payment sx={{ fontSize: 16 }} />}
                          onClick={() => handleFixPaymentStatus(selectedOrder.id)}
                          sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
                        >
                          Fix Payment
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Print + Close row */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      onClick={() => setOpenViewDialog(false)}
                      size="small"
                      sx={{ color: '#888', textTransform: 'none', fontWeight: 500, mr: 'auto' }}
                    >
                      {t('orders.close')}
                    </Button>
                    {/* View Table Invoice — shows all split orders for same table in a print preview */}
                    {selectedOrder?.is_split_payment && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (!selectedOrder) return;
                          const tableNum = (selectedOrder as any).table_number;
                          const origNum = (selectedOrder as any).original_order_number || (selectedOrder as any).order_number;
                          const splitSessionId = (selectedOrder as any).split_session_id;
                          // Find all split orders for this table/session
                          const relatedOrders = displayOrders.filter((o: any) =>
                            o.is_split_payment && (
                              (splitSessionId && o.split_session_id === splitSessionId) ||
                              (o.original_order_number && o.original_order_number === origNum) ||
                              (tableNum && o.table_number === tableNum && o.is_split_payment)
                            )
                          );
                          // Open a print preview window with all split parts
                          const w = window.open('', '_blank', 'width=400,height=600');
                          if (w) {
                            const totalAll = relatedOrders.reduce((s: number, o: any) => s + (o.total || 0), 0);
                            w.document.write(`<html><head><title>Table Invoice — Table ${tableNum}</title>
                              <style>body{font-family:'Poppins',Arial,sans-serif;padding:20px;max-width:360px;margin:0 auto}
                              h1{font-size:18px;text-align:center;border-bottom:2px solid #333;padding-bottom:8px}
                              h2{font-size:14px;color:#7c3aed;margin:12px 0 4px}
                              .item{display:flex;justify-content:space-between;padding:2px 0;font-size:13px}
                              .total{font-weight:bold;border-top:1px solid #333;padding-top:4px;margin-top:4px}
                              .grand{font-size:16px;border-top:2px solid #333;padding-top:8px;margin-top:12px}
                              .split-badge{background:#7c3aed;color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:bold}
                              @media print{button{display:none}}</style></head><body>
                              <h1>📋 TABLE INVOICE<br>Table ${tableNum}${origNum ? ` — Order #${origNum}` : ''}</h1>
                              <p style="text-align:center;font-size:12px;color:#666">${new Date().toLocaleDateString('en-US')} ${new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})} | ${relatedOrders.length} split part(s)</p>`);
                            relatedOrders.forEach((o: any, idx: number) => {
                              w.document.write(`<h2><span class="split-badge">${idx+1}/${relatedOrders.length}</span> ${o.split_person_name || `Part ${idx+1}`} — €${(o.total||0).toFixed(2)} (${o.payment_method || 'cash'})</h2>`);
                              (o.items || []).forEach((it: any) => {
                                w.document.write(`<div class="item"><span>${it.quantity||1}× ${it.menu_item_name||it.name||'Item'}</span><span>€${((it.price||it.unit_price||0)*(it.quantity||1)).toFixed(2)}</span></div>`);
                              });
                              w.document.write(`<div class="item total"><span>Subtotal</span><span>€${(o.total||0).toFixed(2)}</span></div>`);
                            });
                            w.document.write(`<div class="item grand"><span>TOTAL (all parts)</span><span>€${totalAll.toFixed(2)}</span></div>
                              <p style="text-align:center;margin-top:16px"><button onclick="window.print()" style="padding:8px 24px;background:#7c3aed;color:white;border:none;border-radius:6px;font-size:14px;cursor:pointer">🖨️ Print</button></p>
                              </body></html>`);
                            w.document.close();
                          }
                        }}
                        startIcon={<Typography sx={{ fontSize: 14 }}>📋</Typography>}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: '#7c3aed', color: '#7c3aed' }}
                      >
                        Table Invoice
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!selectedOrder}
                      onClick={() => { if (selectedOrder) handlePrintPreview(selectedOrder); }}
                      startIcon={<Preview sx={{ fontSize: 16 }} />}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: '#FF9800', color: '#FF9800' }}
                    >
                      {t('order_details.print_preview')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!selectedOrder}
                      onClick={() => { if (selectedOrder) handlePrintOrder(selectedOrder); }}
                      startIcon={<Print sx={{ fontSize: 16 }} />}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', backgroundColor: '#1976D2' }}
                    >
                      {t('orders.print_order')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* Delivery Assignment Dialog - Same Style as Customer Information Modal */}
          {openDeliveryDialog && (
            <Box
              data-modal-overlay="true"
              onClick={(e) => {
                // Only close if clicking the overlay, not the dialog content
                if (e.target === e.currentTarget) {
                  setOpenDeliveryDialog(false);
                }
              }}
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1300,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'auto'
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#252838',
                  borderRadius: 3,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  border: '1px solid #3a3d52',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: '85vh',
                  overflow: 'auto',
                  pointerEvents: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box sx={{ bgcolor: '#2d3048', p: 2, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('orders.assign_delivery_driver')}
                  </Typography>
                </Box>
                <Box sx={{ p: 3, maxHeight: '70vh', overflowY: 'auto' }}>
                  <Typography variant="body1" sx={{ mb: 2, color: '#d0d0d0' }}>
                    {t('orders.select_driver_for_order')}: {selectedOrder?.order_number || selectedOrder?.id}
                  </Typography>

                  {loadingDrivers ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1, color: '#9ca3af' }}>
                        {t('order_details.loading_drivers')}
                      </Typography>
                    </Box>
                  ) : drivers.length === 0 ? (
                    <Box sx={{
                      p: 3,
                      textAlign: 'center',
                      border: '1px solid #3a3d52',
                      borderRadius: 1,
                      backgroundColor: '#252838'
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('order_details.no_drivers')}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Show available drivers first */}
                      {drivers.filter(driver => driver.is_available && driver.status === 'available').length > 0 && (
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem', color: '#d0d0d0' }}>
                          {t('order_details.available_drivers')} ({drivers.filter(d => d.is_available && d.status === 'available').length})
                        </Typography>
                      )}
                      {drivers.filter(driver => driver.is_available && driver.status === 'available').map((driver) => (
                        <Box
                          key={driver.id}
                          onClick={() => {
                            console.log('🚗 [DRIVER ASSIGNMENT] Driver selected:', {
                              id: driver.id,
                              name: driver.name,
                              email: driver.email,
                              status: driver.status,
                            });
                            setSelectedDriver(driver.id);
                          }}
                          sx={{
                            p: 2,
                            mb: 2,
                            border: '1px solid #3a3d52',
                            borderRadius: 1,
                            backgroundColor: selectedDriver === driver.id ? 'rgba(33, 150, 243, 0.12)' : '#252838',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            '&:hover': {
                              backgroundColor: selectedDriver === driver.id ? 'rgba(33, 150, 243, 0.12)' : '#2d3048',
                              borderColor: selectedDriver === driver.id ? '#1976D2' : '#ccc'
                            }
                          }}
                        >
                          <Person sx={{ color: selectedDriver === driver.id ? '#1976D2' : '#666' }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#d0d0d0' }}>
                              {driver.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {driver.phone} • {driver.vehicle_type} • ⭐ {driver.rating?.toFixed(1) || '5.0'}
                            </Typography>
                          </Box>
                          {selectedDriver === driver.id ? (
                            <Chip
                              label={t('orders.selected')}
                              color="primary"
                              size="small"
                              sx={{ backgroundColor: '#1976D2', color: 'white' }}
                            />
                          ) : (
                            <Chip
                              label={t('order_details.available')}
                              color="success"
                              size="small"
                              sx={{ backgroundColor: '#28a745', color: 'white' }}
                            />
                          )}
                        </Box>
                      ))}

                      {/* Show busy/offline drivers (optional) */}
                      {drivers.filter(driver => !driver.is_available || driver.status !== 'available').length > 0 && (
                        <>
                          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 600, fontSize: '1rem', color: '#9ca3af' }}>
                            {t('order_details.busy_offline')} ({drivers.filter(d => !d.is_available || d.status !== 'available').length})
                          </Typography>
                          {drivers.filter(driver => !driver.is_available || driver.status !== 'available').map((driver) => (
                            <Box
                              key={driver.id}
                              sx={{
                                p: 2,
                                mb: 2,
                                border: '1px solid #3a3d52',
                                borderRadius: 1,
                                backgroundColor: '#2d3048',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                opacity: 0.6
                              }}
                            >
                              <Person sx={{ color: '#999' }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#9ca3af' }}>
                                  {driver.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {driver.phone} • {driver.vehicle_type}
                                </Typography>
                              </Box>
                              <Chip
                                label={driver.status === 'busy' ? t('order_details.busy') : t('order_details.offline')}
                                size="small"
                                sx={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  opacity: 0.7
                                }}
                                disabled
                              />
                            </Box>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Box>
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end',
                  p: 2,
                  bgcolor: '#2d3048',
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12
                }}>
                  <Button
                    onClick={() => setOpenDeliveryDialog(false)}
                    variant="text"
                    sx={{ color: '#9ca3af' }}
                  >
                    {t('orders.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!selectedDriver}
                    onClick={handleAssignDriver}
                    sx={{
                      backgroundColor: '#28a745',
                      '&:hover': { backgroundColor: '#218838' },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                        color: '#9ca3af'
                      }
                    }}
                  >
                    {t('orders.assign_driver')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Order notifications removed — handled via Firestore */}

          {/* Edit Order Item Modal */}
          {editingItem && (
            <EditOrderItemModal
              open={openEditItemModal}
              onClose={() => {
                setOpenEditItemModal(false);
                setEditingItem(null);
              }}
              item={editingItem.item}
              itemIndex={editingItem.index}
              onSave={handleSaveEditedItem}
              language={language}
            />
          )}

          {/* Edit Customer Info Modal */}
          <Dialog
            open={openEditCustomerModal}
            onClose={() => {
              if (!updatingCustomer) {
                setOpenEditCustomerModal(false);
              }
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {t('orders.edit_customer_information')}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label={t('orders.customer_name')}
                  value={editingCustomerName}
                  onChange={(e) => setEditingCustomerName(e.target.value)}
                  fullWidth
                  required
                  disabled={updatingCustomer}
                  helperText={t('orders.enter_customer_name')}
                />
                <TextField
                  label={t('orders.customer_address')}
                  value={editingCustomerAddress}
                  onChange={(e) => setEditingCustomerAddress(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  disabled={updatingCustomer}
                  helperText={t('orders.enter_customer_address')}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenEditCustomerModal(false);
                  setEditingCustomerName('');
                  setEditingCustomerAddress('');
                }}
                disabled={updatingCustomer}
              >
                {t('orders.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  if (!selectedOrder || !editingCustomerName.trim()) {
                    return;
                  }

                  try {
                    setUpdatingCustomer(true);
                    const firebaseOrderService = (await import('../../services/firebaseOrderService')).default;
                    const service = firebaseOrderService.getInstance();

                    // Update order in Firebase
                    await service.updateOrderCustomerInfo(
                      selectedOrder.id,
                      editingCustomerName.trim(),
                      editingCustomerAddress.trim() || undefined
                    );

                    // Update local state
                    setOrders(orders.map(order => 
                      order.id === selectedOrder.id
                        ? {
                            ...order,
                            customer_name: editingCustomerName.trim(),
                            ...(editingCustomerAddress.trim() && {
                              customer_address: editingCustomerAddress.trim()
                            })
                          } as any
                        : order
                    ));

                    // Update selected order
                    setSelectedOrder({
                      ...selectedOrder,
                      customer_name: editingCustomerName.trim(),
                      ...(editingCustomerAddress.trim() && {
                        customer_address: editingCustomerAddress.trim()
                      })
                    } as any);

                    // Log success (notification modal disabled)
                    console.log(`✅ Customer information updated successfully for order ${selectedOrder.order_number}`);

                    // Close modal
                    setOpenEditCustomerModal(false);
                    setEditingCustomerName('');
                    setEditingCustomerAddress('');

                  } catch (error: any) {
                    console.error('❌ [EDIT CUSTOMER] Failed to update customer info:', error);
                    alert(`Failed to update customer information: ${error.message || 'Unknown error'}`);
                  } finally {
                    setUpdatingCustomer(false);
                  }
                }}
                variant="contained"
                disabled={updatingCustomer || !editingCustomerName.trim()}
                startIcon={updatingCustomer ? <CircularProgress size={16} /> : <Done />}
              >
                {updatingCustomer ? t('orders.updating') : t('orders.save')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>

      {/* FRAUD PREVENTION #2: Manager PIN dialog */}
      {managerPinAction && (
        <ManagerPinDialog
          open={managerPinOpen}
          actionLabel={managerPinAction.label}
          requireReason={managerPinAction.requireReason}
          onApproved={(email, reason) => {
            setManagerPinOpen(false);
            const cb = managerPinAction.onApproved;
            setManagerPinAction(null);
            cb(email, reason);
          }}
          onClose={() => {
            setManagerPinOpen(false);
            setManagerPinAction(null);
          }}
        />
      )}

      {/* Void-Item Confirmation Dialog (works for any order type) */}
      {voidItemTarget && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => { if (!voidItemBusy) { setVoidItemTarget(null); setVoidItemReason(''); } }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#252838', borderRadius: 3, p: 3, minWidth: 340, maxWidth: 440,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)', border: '1px solid #3a3d52',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#fff', fontFamily: '"Poppins", sans-serif' }}>
              Remove item from order?
            </Typography>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.85rem', mb: 2 }}>
              Order <span style={{ color: '#00C896', fontWeight: 700 }}>#{voidItemTarget.orderNumber}</span> · this action is logged in the audit trail.
            </Typography>
            <Box sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.10)', border: '1px solid rgba(211, 47, 47, 0.35)',
              borderRadius: 1.5, p: 1.5, mb: 2,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Typography sx={{ color: '#fff', fontWeight: 600 }}>{voidItemTarget.itemLabel}</Typography>
              <Typography sx={{ color: '#ff8a80', fontWeight: 700 }}>−€{voidItemTarget.itemPrice.toFixed(2)}</Typography>
            </Box>
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', mb: 0.6 }}>
              Reason (required)
            </Typography>
            <Box
              component="textarea"
              value={voidItemReason}
              onChange={(e: any) => setVoidItemReason(e.target.value)}
              placeholder="e.g. wrong item, customer changed mind, kitchen unavailable…"
              rows={2}
              sx={{
                width: '100%', resize: 'vertical', minHeight: 56,
                backgroundColor: '#1a1d2e', color: '#e0e0e0',
                border: '1px solid #3a3d52', borderRadius: 1.5,
                p: 1, fontFamily: 'inherit', fontSize: '0.85rem',
                outline: 'none',
                '&:focus': { borderColor: '#00C896' },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
              <Box
                role="button"
                onClick={() => { if (!voidItemBusy) { setVoidItemTarget(null); setVoidItemReason(''); } }}
                sx={{
                  px: 2.2, py: 1, borderRadius: 1.5, cursor: 'pointer',
                  color: '#9ca3af', fontWeight: 600, fontSize: '0.85rem',
                  border: '1px solid #3a3d52',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                }}
              >
                Cancel
              </Box>
              <Box
                role="button"
                onClick={() => { if (!voidItemBusy) handleVoidOrderItem(); }}
                sx={{
                  px: 2.2, py: 1, borderRadius: 1.5, cursor: voidItemBusy ? 'not-allowed' : 'pointer',
                  backgroundColor: voidItemBusy ? '#7a1f1f' : '#d32f2f', color: '#fff',
                  fontWeight: 700, fontSize: '0.85rem',
                  opacity: voidItemBusy ? 0.7 : 1,
                  '&:hover': { backgroundColor: voidItemBusy ? '#7a1f1f' : '#b71c1c' },
                }}
              >
                {voidItemBusy ? 'Removing…' : 'Remove item'}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Apply Discount Dialog (POS-only) */}
      {discountDialog.open && discountDialog.order && (
        <Box
          onClick={() => setDiscountDialog({ open: false, order: null })}
          sx={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1500, backgroundColor: 'rgba(0,0,0,0.6)',
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: '#1a1d2e', borderRadius: 3,
              border: '1px solid #3a3d52',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              maxWidth: 460, width: '90%', overflow: 'hidden',
            }}
          >
            <Box sx={{
              p: 2.5, background: 'linear-gradient(135deg, #00C896 0%, #7bc912 100%)',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <LocalOffer sx={{ color: '#1a1d2e', fontSize: 28 }} />
              <Typography sx={{ fontWeight: 800, color: '#1a1d2e', fontSize: '1.1rem' }}>
                {t('orders.discount_dialog_title')} · #{discountDialog.order.order_number}
              </Typography>
            </Box>
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography sx={{ color: '#cbd5e1', fontSize: '0.8rem', mb: 0.75, fontWeight: 600 }}>
                  {t('orders.discount_type')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth variant={discountTypeInput === 'percentage' ? 'contained' : 'outlined'}
                    onClick={() => setDiscountTypeInput('percentage')}
                    sx={{
                      textTransform: 'none', fontWeight: 700,
                      backgroundColor: discountTypeInput === 'percentage' ? '#00C896' : 'transparent',
                      color: discountTypeInput === 'percentage' ? '#1a1d2e' : '#00C896',
                      borderColor: '#00C896',
                      '&:hover': { backgroundColor: discountTypeInput === 'percentage' ? '#7bc912' : 'rgba(0, 200, 150,0.1)' },
                    }}
                  >
                    % {t('orders.discount_percentage')}
                  </Button>
                  <Button
                    fullWidth variant={discountTypeInput === 'fixed' ? 'contained' : 'outlined'}
                    onClick={() => setDiscountTypeInput('fixed')}
                    sx={{
                      textTransform: 'none', fontWeight: 700,
                      backgroundColor: discountTypeInput === 'fixed' ? '#00C896' : 'transparent',
                      color: discountTypeInput === 'fixed' ? '#1a1d2e' : '#00C896',
                      borderColor: '#00C896',
                      '&:hover': { backgroundColor: discountTypeInput === 'fixed' ? '#7bc912' : 'rgba(0, 200, 150,0.1)' },
                    }}
                  >
                    € {t('orders.discount_fixed')}
                  </Button>
                </Box>
              </Box>
              <TextField
                fullWidth size="small" type="number"
                label={t('orders.discount_value')}
                placeholder={discountTypeInput === 'percentage' ? '10' : '5.00'}
                value={discountValueInput}
                onChange={(e) => setDiscountValueInput(e.target.value)}
                inputProps={{ min: 0, step: discountTypeInput === 'percentage' ? 1 : 0.5, max: discountTypeInput === 'percentage' ? 100 : undefined }}
                InputLabelProps={{ sx: { color: '#cbd5e1' } }}
                sx={{
                  '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                }}
              />
              <TextField
                fullWidth size="small"
                label={t('orders.discount_reason')}
                value={discountReasonInput}
                onChange={(e) => setDiscountReasonInput(e.target.value)}
                InputLabelProps={{ sx: { color: '#cbd5e1' } }}
                sx={{
                  '& .MuiOutlinedInput-root': { color: '#fff', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                }}
              />
            </Box>
            <Box sx={{ p: 2, borderTop: '1px solid #3a3d52', display: 'flex', gap: 1, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(discountDialog.order as any).discount_amount > 0 && (
                  <Button
                    onClick={handleRemoveOrderDiscount}
                    disabled={applyingDiscount}
                    variant="outlined"
                    sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#f44336', color: '#f44336' }}
                  >
                    {t('orders.remove_discount')}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setDiscountDialog({ open: false, order: null })}
                  disabled={applyingDiscount}
                  sx={{ textTransform: 'none', color: '#cbd5e1' }}
                >
                  {t('orders.cancel') || 'Cancel'}
                </Button>
                <Button
                  onClick={handleApplyOrderDiscount}
                  disabled={applyingDiscount || !discountValueInput}
                  variant="contained"
                  sx={{
                    textTransform: 'none', fontWeight: 700,
                    backgroundColor: '#00C896', color: '#1a1d2e',
                    '&:hover': { backgroundColor: '#7bc912' },
                  }}
                >
                  {t('orders.apply_discount')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ModernOrdersPage;
